import React, { useState, useEffect } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import { FaSave, FaPlus, FaTrash, FaSearch, FaBuilding, FaChevronDown, FaChevronUp, FaLink, FaTimes, FaUniversity, FaStar, FaRegStar, FaEdit, FaDownload, FaCopy, FaEye, FaClipboardCheck } from 'react-icons/fa';
import FormButton from '../../components/FormButton';
import Alert from '../../components/Alert';
import api from '../../services/api';
import { useHistory, useLocation } from 'react-router-dom';
import { buscarCep, extract } from '../../util/util';
import ReactSelect from '../../components/ReactSelect';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { FinField, FinInput, FinTextarea, FinCheck, FinChecks } from '../../components/FinField';
import {
    TIPOS_FORNECEDOR, DOCUMENTOS_FISCAIS, REGIMES_IRPJ, REGIMES_IBS_CBS,
    REGIMES_ESPECIFICOS, DESTAQUE_DOCUMENTOS, SIM_NAO, opcoesDe, placeholderDe, opcoesSemestre,
    TIPOS_DOCUMENTO, rotuloDe, PAPEIS_CONTATO, PRAZOS_XML, PORTES_EMPRESA, rotuloDoCaminhoNaoAplica,
    opcoesComValoresGravados,
} from '../../util/fornecedorCadastro';
import '../../css/financeiro-parcela-pagar.css';

// Mesma forma do contato do formulário público: `cargo` é a ÁREA (comercial/
// fiscal/financeiro/cbs_ibs) e `funcao` é o cargo real da pessoa, que o anexo pede
// só para o responsável por CBS/IBS — é ele que distingue um funcionário do
// contador terceirizado.
//
// `funcao` PRECISA existir aqui mesmo sendo um campo que só o formulário coleta:
// esta tela reconstrói o contato a partir das próprias chaves ao salvar, então
// chave que ela não conhece é chave que ela apaga. Abrir e salvar um fornecedor
// que veio do formulário zerava o `funcao` sem aviso.
const criarContatoVazio = () => ({
    cargo: '',
    nome: '',
    funcao: '',
    email: '',
    celular: '',
    observacao: '',
    // Qual contato alimenta as colunas legadas `email` / `contato` / `celular` do
    // participante, que o Financeiro lê. Antes era sempre o primeiro da lista, e o
    // formulário público semeia o comercial em primeiro — então o comercial virava
    // o e-mail do cadastro sem ninguém ter escolhido isso. Ver o `saving()` do
    // model `Participante`.
    principal: false,
});

/**
 * Opções da Área do contato: os quatro papéis do anexo e, quando o cadastro tem
 * cargo digitado à mão de antes do vocabulário existir, o próprio valor gravado.
 *
 * Sem essa injeção o select abriria vazio nesses cadastros e o save gravaria vazio
 * por cima — trocar texto livre por lista fechada não pode custar o que já está
 * lá. É a mesma tolerância do `rotuloDe`, que devolve o código quando ele não está
 * no mapa em vez de virar traço.
 */
const opcoesAreaContato = (valorAtual) => {
    const base = opcoesDe(PAPEIS_CONTATO);
    const valor = String(valorAtual ?? '').trim();

    return valor === '' || base.some((opcao) => opcao.value === valor)
        ? base
        : [...base, { value: valor, label: valor }];
};

// Colunas que ESTA tela só EXIBE, e por isso não podem voltar no PUT.
//
// `observacao_<tributo>` é declaração do fornecedor, gravada quando a submissão é
// aprovada; `proxima_revisao` é carimbada pelo mesmo caminho. O estado veio do GET
// da abertura da tela e nunca é editado aqui — reenviá-las regrava o valor velho
// (null, se na abertura ainda não havia nada) por cima do que a aprovação gravou
// depois. Basta a aba estar aberta desde antes da aprovação.
//
// Chave ausente o `$request->only()` não inclui, então a coluna não é tocada. Isso
// é diferente de mandar null: null é limpeza deliberada, e continua funcionando para
// os campos que o operador de fato edita.
//
// `observacao_fiscal` NÃO entra aqui: essa é do operador, tem textarea na tela, e
// precisa continuar podendo ser limpa.
const CLASSIFICACAO_SOMENTE_LEITURA = [
    'observacao_irrf', 'observacao_pis', 'observacao_cofins', 'observacao_csll',
    'observacao_inss', 'observacao_iss', 'observacao_icms_st', 'observacao_funrural_senar',
    'proxima_revisao',
];

const criarClassificacaoFiscalVazia = () => ({
    nbs: '',
    nbs_descricao: '',
    // Seção 3 do anexo. Moraram em `participante` até a consolidação das migrations; vieram para
    // cá porque `participante` é compartilhada com o convidado e as duas ficavam nulas em quase
    // toda linha. O campo na tela segue na aba de identificação — ver a nota lá.
    email_xml: '',
    prazo_envio_xml: '',
    // Seção 1 do anexo — só o porte fica aqui. CNAE principal, CNAEs secundários e
    // data de constituição declarados pelo fornecedor gravam em `fornecedor_cnae`
    // (`origem = 'declarado'`) e aparecem na aba "CNAE / NBS".
    porte: '',
    retem_irrf: false, aliquota_irrf: '', esfera_irrf: 'Federal',
    retem_iss: false, aliquota_iss: '', esfera_iss: 'Municipal',
    retem_pis: false, aliquota_pis: '', esfera_pis: 'Federal',
    retem_cofins: false, aliquota_cofins: '', esfera_cofins: 'Federal',
    retem_csll: false, aliquota_csll: '', esfera_csll: 'Federal',
    retem_inss: false, aliquota_inss: '', esfera_inss: 'Federal',
    retem_ibs: false, aliquota_ibs: '', esfera_ibs: 'Estadual + Municipal',
    retem_cbs: false, aliquota_cbs: '', esfera_cbs: 'Federal',
    // Seção 7 do anexo lista nove tributos; a grade tinha oito cards. Estes dois
    // ganharam coluna nesta entrega e o formulário público já os coleta — sem card
    // aqui, o fornecedor declarava e o operador não via nem corrigia.
    retem_icms_st: false, aliquota_icms_st: '', esfera_icms_st: 'Estadual',
    retem_funrural_senar: false, aliquota_funrural_senar: '', esfera_funrural_senar: 'Federal',
    // "Nenhuma retenção incide" — a declaração de AUSÊNCIA da seção 7, que o
    // formulário público coleta num checkbox próprio.
    //
    // Precisa existir aqui mesmo sendo declaração do fornecedor: a coluna está no
    // `$fillable`, entra no estado pelo spread do GET e volta no PUT. Sem controle
    // na tela, o operador marcava um tributo na grade e salvava um registro que
    // dizia as duas coisas — "nada incide" e "retém IRRF". O `aprovar()` reconcilia
    // isso; o `FornecedorClassificacaoFiscalController::upsert` não.
    sem_retencoes: false,
    data_transicao_iva: '',
    observacao_fiscal: '',
    ncm: '',
    retencoes_outras: '',
    // Seções 4, 5 e 6 do anexo. Entravam só pelo formulário público: o operador
    // via no modal de revisão uma vez e depois não tinha onde consultar nem corrigir.
    tipo_fornecedor: '',
    documentos_fiscais: [],
    documentos_fiscais_outro: '',
    itens_fornecidos: '',
    locais_execucao: '',
    regime_irpj: '',
    regime_irpj_fundamento: '',
    regime_ibs_cbs: '',
    vigencia_opcao: '',
    data_opcao_simples: '',
    opcao_proxima_limite: '',
    regime_especifico: '',
    regime_especifico_outro: '',
    reducao_percentual: '',
    contribuinte_iss: '',
    destaque_documentos: '',
    cst: '',
    cclasstrib: '',
    credito_presumido_aplica: '',
    diferimento_aplica: '',
    diferimento_suspensao: '',
    // Seção 11 do anexo — controle cadastral interno. Fica aqui, e não só no modal
    // da fila de revisão, porque fornecedor que nunca recebe o formulário também
    // precisa ser classificado: dos 37 já cadastrados, nenhum teria por onde.
    classificacao_credito: '',
    credito_percentual: '',
    periodicidade_meses: '',
});

const criarNovoNbsManualVazio = () => ({
    codigo: '',
    descricao: '',
});

const criarContaBancariaVazia = () => ({
    banco: null,
    nomeBancoOption: null,
    nome_banco_snapshot: '',
    agencia: '',
    conta: '',
    tipo_conta: 'corrente',
    titular: '',
    pix_chave: '',
    pix_tipo: '',
    principal: false,
    observacao: '',
});

const OPCOES_TIPO_CONTA = [
    { value: 'corrente', label: 'Conta corrente' },
    { value: 'poupanca', label: 'Conta poupança' },
    { value: 'pagamento', label: 'Conta pagamento' },
    { value: 'salario', label: 'Conta salário' },
];

const OPCOES_TIPO_PIX = [
    { value: 'cpf', label: 'CPF' },
    { value: 'cnpj', label: 'CNPJ' },
    { value: 'email', label: 'E-mail' },
    { value: 'telefone', label: 'Telefone' },
    { value: 'aleatoria', label: 'Chave aleatória' },
];

const OPCOES_SITUACAO = [
    { value: 'ativo', label: 'Ativo' },
    { value: 'inativo', label: 'Inativo' },
];

const OPCOES_SIM_NAO = [
    { value: 'nao', label: 'Não' },
    { value: 'sim', label: 'Sim' },
];

const OPCOES_TIPO_RECEBIMENTO = [
    { value: 'boleto', label: 'Boleto' },
    { value: 'pix', label: 'PIX' },
    { value: 'transferencia', label: 'Transferência' },
    { value: 'deposito', label: 'Depósito' },
    { value: 'cartao', label: 'Cartão' },
    { value: 'outro', label: 'Outro' },
];

const OPCOES_TIPO_EVENTO = [
    { value: 'logistica', label: 'Logística' },
    { value: 'producao', label: 'Produção' },
];

const OPCOES_INDICADOR_INSCRICAO = [
    { value: '1', label: 'Contribuinte' },
    { value: '2', label: 'Isento' },
    { value: '9', label: 'Não contribuinte' },
];

const OPCOES_REGIME_TRIBUTARIO = [
    { value: '1', label: 'MEI' },
    { value: '2', label: 'Simples Nacional' },
    { value: '3', label: 'Simples Nacional (Sublimite)' },
    { value: '4', label: 'Lucro Presumido' },
];

const limparCnpj = (cnpj) => String(cnpj ?? '').replace(/\D/g, '');

const criarDadosIniciais = () => ({
    tipoCadastro: 'orcamento',
    situacaoRegistro: 'ativo',
    preferencial: 'nao',
    pagamentoantecipado: 'nao',
    tiporecebimento: 'boleto',
    tipoeventofornecedor: '',
    fornecedorgrupos: [],
    fornecedorgruposOptions: [],
    contatosfornecedor: [criarContatoVazio()],
    tiposervico: null,
    tiposervicoOption: null,
    // "Tipo de Subserviço": mesma lista do Tipo de Serviço, vários por fornecedor (N:N)
    subservicosOptions: [],
    sede: false,
});

// Aba Formulário. Mesma lista do disparo no back (FornecedorDisparoController::PERFIS_DISPARO):
// o link dá acesso ao formulário em nome do fornecedor.
const PERFIS_FORMULARIO = ['admin', 'diretor', 'gerenteprojeto', 'gerentefinanceiro', 'suplier'];

const FORMATOS_COPIA = [
    { key: 'email', label: 'E-mail completo', ajuda: 'O corpo vai formatado, pronto para colar no Gmail ou no Outlook.' },
    { key: 'botao', label: 'Botão', ajuda: 'O botão "Preencher cadastro" com o link, para colar dentro de uma mensagem sua.' },
    { key: 'link', label: 'Link', ajuda: 'Só o endereço, para WhatsApp ou qualquer lugar que aceite apenas texto.' },
];

// Mesmos rótulos e cores da coluna Formulário do Painel de Fornecedores
const pillFormulario = (formulario) => {
    if (formulario.status === 'aprovado') return { rotulo: 'Aprovado', classe: 'is-ok' };
    if (formulario.status === 'em_revisao') return { rotulo: 'Em revisão', classe: 'is-info' };
    if (formulario.status === 'gerado') return { rotulo: 'Link gerado', classe: 'is-ok' };
    if (formulario.status === 'enviado') return { rotulo: 'Enviado', classe: 'is-ok' };
    return { rotulo: 'Não enviado', classe: 'is-muted' };
};

const dataHoraBr = (valor) => {
    if (!valor) return '';
    const [data, hora] = String(valor).replace('T', ' ').split(' ');
    const [ano, mes, dia] = String(data).split('-');
    if (!ano || !mes || !dia) return String(valor);
    return dia + '/' + mes + '/' + ano + (hora ? ' ' + hora.slice(0, 5) : '');
};

// HTML copiado como texto formatado: colado num e-mail, vira o e-mail/botão pronto. Sem a
// API de clipboard com HTML (navegador antigo), copia pela seleção de um elemento fora da tela.
const copiarHtml = async (html, textoPuro) => {
    if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([new window.ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([textoPuro], { type: 'text/plain' }),
        })]);
        return;
    }
    const div = document.createElement('div');
    div.contentEditable = 'true';
    div.style.position = 'fixed';
    div.style.left = '-9999px';
    div.innerHTML = html;
    document.body.appendChild(div);
    const selecao = window.getSelection();
    const faixa = document.createRange();
    faixa.selectNodeContents(div);
    selecao.removeAllRanges();
    selecao.addRange(faixa);
    const copiou = document.execCommand('copy');
    selecao.removeAllRanges();
    document.body.removeChild(div);
    if (!copiou) throw new Error('copia');
};

export default function CadastroFornecedor({ match }) {
    const id = match.params.id;
    const token = localStorage.getItem('token');
    const [tab, setTab] = useState('geral');
    const [dados, setDados] = useState(criarDadosIniciais());
    const [tipoServicoOpcoes, setTipoServicoOpcoes] = useState([]);
    const [salvando, setSalvando] = useState(false);
    const [classificacaoFiscal, setClassificacaoFiscal] = useState(criarClassificacaoFiscalVazia());
    // Secao 9: o que o fornecedor ja entregou, de todas as vezes que preencheu. A
    // fila de revisao mostra os anexos de UMA submissao e some quando ela sai da
    // fila; depois disso nao havia tela nenhuma que mostrasse os documentos.
    const [documentos, setDocumentos] = useState([]);

    // Aba Formulário: status do formulário de cadastro, o link que o fornecedor abre hoje e
    // as três formas de copiá-lo para mandar por fora do sistema.
    const podeVerFormulario = PERFIS_FORMULARIO.includes(localStorage.getItem('perfil'));
    const [formulario, setFormulario] = useState(null);
    const [formularioCarregando, setFormularioCarregando] = useState(false);
    const [gerandoLink, setGerandoLink] = useState(false);
    const [formatoCopia, setFormatoCopia] = useState('email');
    const [previaFormulario, setPreviaFormulario] = useState(false);

    const carregarFormulario = () => {
        setFormularioCarregando(true);
        api({ method: 'get', action: '/fornecedor/' + id + '/formulario', token: token })
            .then(response => setFormulario(response.data?.data ?? null))
            .catch(() => toast.error('Não foi possível carregar o formulário.'))
            .finally(() => setFormularioCarregando(false));
    };

    useEffect(() => {
        if (tab === 'formulario' && id && !formulario) {
            carregarFormulario();
        }
        // eslint-disable-next-line
    }, [tab]);

    const gerarLinkFormulario = () => {
        Alert({
            icon: 'question',
            title: 'Gerar novo link',
            html: 'Nenhum e-mail é enviado: o link fica aqui para você mandar por fora. O fornecedor passa a '
                + 'constar como "Link gerado" e a dever o formulário.'
                + (formulario?.status === 'em_revisao' ? ' O formulário que ele já devolveu continua na fila de revisão.' : '')
                + ' Os links anteriores seguem valendo até expirar.',
            confirmButton: 'Gerar link',
            cancelButton: 'Cancelar',
            callBack: (result) => {
                if (!result.isConfirmed) return;
                setGerandoLink(true);
                api({ method: 'post', action: '/fornecedor/' + id + '/formulario/gerar', token: token })
                    .then(response => {
                        setFormulario(response.data?.data ?? null);
                        toast.success('Link gerado.');
                    })
                    .catch(() => toast.error('Não foi possível gerar o link.'))
                    .finally(() => setGerandoLink(false));
            },
        });
    };

    const copiarFormulario = async () => {
        if (!formulario?.link) return;
        try {
            if (formatoCopia === 'link') {
                await navigator.clipboard.writeText(formulario.link);
            } else {
                await copiarHtml(formatoCopia === 'email' ? formulario.email_html : formulario.botao_html, formulario.link);
            }
            toast.success(formatoCopia === 'email' ? 'E-mail copiado.' : formatoCopia === 'botao' ? 'Botão copiado.' : 'Link copiado.');
        } catch (e) {
            toast.error('Não foi possível copiar. Use a pré-visualização e copie manualmente.');
        }
    };

    const [cnaes, setCnaes] = useState([]);
    const [cnpjBuscando, setCnpjBuscando] = useState(false);
    const [cnaeExpandidoId, setCnaeExpandidoId] = useState(null);
    const [novoCnaeManualVisivel, setNovoCnaeManualVisivel] = useState(false);
    const [novoCnaeManual, setNovoCnaeManual] = useState({ codigo: '', descricao: '' });
    // estado por-CNAE para formulário "Adicionar NBS manual" inline
    const [novoNbsManualPorCnae, setNovoNbsManualPorCnae] = useState({});
    // sugestões automáticas de NBS por CNAE (cache)
    const [sugestoesPorCnae, setSugestoesPorCnae] = useState({});
    const [sugestoesSelecionadasPorCnae, setSugestoesSelecionadasPorCnae] = useState({});
    // contas bancárias
    const [bancos, setBancos] = useState([]);
    const [novoBancoVisivel, setNovoBancoVisivel] = useState(false);
    const [bancoEmEdicao, setBancoEmEdicao] = useState(criarContaBancariaVazia());
    const [bancoEditandoId, setBancoEditandoId] = useState(null);
    // modal de cadastro de novo banco no catálogo financeirobanco
    const [modalNovoBancoCatalogo, setModalNovoBancoCatalogo] = useState(false);
    const [novoBancoCatalogo, setNovoBancoCatalogo] = useState({ codigo: '', nome: '' });
    const [salvandoBancoCatalogo, setSalvandoBancoCatalogo] = useState(false);
    // contador para forçar reload do AsyncSelect quando criar banco novo
    const [bancoSelectKey, setBancoSelectKey] = useState(0);
    const history = useHistory();
    let { state } = useLocation();
    const tipoCadastro = state?.tipoCadastro ?? 'orcamento';

    const preencherCep = (cep, dadosAtuais) => {
        buscarCep(cep).then((resposta) => {
            if (!resposta.erro) {
                setDados({
                    ...dadosAtuais,
                    ...{
                        enderecobairro: resposta.bairro,
                        enderecocomplemento: resposta.complemento,
                        enderecologradouro: resposta.logradouro,
                        enderecopais: '30',
                        nomeEnderecopais: 'Brasil',
                        enderecomunicipio: resposta.ibge,
                        nomeEnderecomunicipio: resposta.localidade
                    }
                });
            }
        });
    };

    const normalizarContatosRecebidos = (dadosApi) => {
        if (Array.isArray(dadosApi.contatosfornecedor) && dadosApi.contatosfornecedor.length > 0) {
            return dadosApi.contatosfornecedor.map((contato) => ({
                cargo: contato.cargo ?? contato.contato ?? '',
                nome: contato.nome ?? '',
                funcao: contato.funcao ?? '',
                email: contato.email ?? '',
                celular: contato.celular ?? contato.telefone ?? '',
                observacao: contato.observacao ?? '',
                principal: Boolean(contato.principal),
            }));
        }

        if (dadosApi.contato || dadosApi.email || dadosApi.celular || dadosApi.telefone) {
            return [{
                cargo: '',
                nome: '',
                funcao: '',
                email: dadosApi.email ?? '',
                celular: dadosApi.celular ?? dadosApi.telefone ?? '',
                observacao: '',
            }];
        }

        return [criarContatoVazio()];
    };

    const buscar = () => {
        if (id) {
            api({
                method: 'get',
                action: '/participante/' + id,
                token: token,
            }).then(response => {
                const dadosApi = response.data.data;
                const dadosRecebidos = extract({
                    jsonLaravel: dadosApi,
                    chaves: [
                        'enderecopais',
                        'aereomunicipioembarque',
                        'enderecomunicipio',
                        'banco',
                        'categoria',
                        'fornecedorgrupo',
                    ]
                });

                delete dadosRecebidos.tipoparticipante;

                const fornecedorGruposSelecionados = (dadosApi.fornecedorgrupos ?? []).map((registro) => ({
                    value: registro.id,
                    label: registro.nome,
                }));

                setDados({
                    ...criarDadosIniciais(),
                    ...dadosRecebidos,
                    tipoCadastro: 'orcamento',
                    fornecedorgrupos: fornecedorGruposSelecionados.map((registro) => registro.value),
                    fornecedorgruposOptions: fornecedorGruposSelecionados,
                    fornecedorgrupo: fornecedorGruposSelecionados[0]?.value ?? dadosRecebidos.fornecedorgrupo ?? null,
                    nomeFornecedorgrupo: fornecedorGruposSelecionados[0]?.label ?? dadosRecebidos.nomeFornecedorgrupo ?? null,
                    tiposervico: dadosRecebidos.tiposervico ? String(dadosRecebidos.tiposervico).split(',').filter(Boolean)[0] : null,
                    tiposervicoOption: (dadosApi.tiposervicogrupos ?? []).length > 0 ? {
                        value: dadosApi.tiposervicogrupos[0].id,
                        label: ((dadosApi.tiposervicogrupos[0].codigo ?? '') ? (dadosApi.tiposervicogrupos[0].codigo + ' - ') : '') + dadosApi.tiposervicogrupos[0].nome,
                    } : null,
                    subservicosOptions: (dadosApi.subservicos ?? []).map((registro) => ({
                        value: registro.id,
                        label: (registro.codigo ? registro.codigo + ' - ' : '') + registro.nome,
                    })),
                    contatosfornecedor: normalizarContatosRecebidos(dadosApi),
                    sede: Boolean(dadosApi.sede),
                    tipoeventofornecedor: dadosApi.tipoeventofornecedor ?? '',
                });
            }).catch(err => {
                //console.log(err)
            });

            api({
                method: 'get',
                action: '/participante/' + id + '/classificacaoFiscal',
                token: token,
            }).then(response => {
                const registro = response.data?.data;
                if (registro) {
                    setClassificacaoFiscal({
                        ...criarClassificacaoFiscalVazia(),
                        ...registro,
                        data_transicao_iva: registro.data_transicao_iva
                            ? String(registro.data_transicao_iva).substring(0, 10)
                            : '',
                    });
                }
            }).catch(() => {});

            api({
                method: 'get',
                action: '/participante/' + id + '/documentosFornecedor',
                token: token,
            }).then(response => {
                setDocumentos(response.data?.data ?? []);
            }).catch(() => {});

            carregarCnaes();
            carregarBancos();
        } else {
            setDados({
                ...criarDadosIniciais(),
                tipoCadastro: 'orcamento',
            });
        }
    };

    const carregarTipoServicoOpcoes = () => {
        api({
            method: 'get',
            action: '/produto',
            token: token,
            params: {
                situacaoRegistro: 'ativo',
                modulo: 'servicoOrcamento',
                superiorIsNull: true,
            }
        }).then((response) => {
            setTipoServicoOpcoes(
                (response.data.data ?? []).map((registro) => ({
                    value: registro.id,
                    label: registro.codigo + ' - ' + registro.nome,
                }))
            );
        }).catch((err) => {
            //console.log(err)
        });
    };

    const carregarCnaes = () => {
        if (!id) return;
        api({
            method: 'get',
            action: '/participante/' + id + '/cnaes',
            token: token,
        }).then((response) => {
            setCnaes(response.data?.data ?? []);
        }).catch(() => {});
    };

    const buscarReceita = () => {
        const cnpj = limparCnpj(dados.cnpj);
        if (cnpj.length !== 14) {
            toast.error('Informe um CNPJ válido (14 dígitos) antes de buscar na Receita.');
            return;
        }
        if (!id) {
            toast.info('Salve o fornecedor uma vez antes de buscar dados na Receita Federal.');
            return;
        }

        setCnpjBuscando(true);
        api({
            method: 'get',
            action: '/cnpj/' + cnpj,
            token: token,
        }).then((response) => {
            const receita = response.data?.data;
            if (!receita || receita.erro) {
                toast.error(receita?.erro ?? 'Não foi possível obter dados da Receita.');
                return;
            }

            // Pre-preenche apenas campos vazios — não sobrescreve o que o operador já editou
            const atualizacao = {};
            if (!dados.nome && receita.razao_social) atualizacao.nome = receita.razao_social;
            if (!dados.nomefantasia && receita.nome_fantasia) atualizacao.nomefantasia = receita.nome_fantasia;
            const end = receita.endereco ?? {};
            if (!dados.enderecologradouro && end.logradouro) atualizacao.enderecologradouro = end.logradouro;
            if (!dados.endereconumero && end.numero) atualizacao.endereconumero = end.numero;
            if (!dados.enderecocomplemento && end.complemento) atualizacao.enderecocomplemento = end.complemento;
            if (!dados.enderecobairro && end.bairro) atualizacao.enderecobairro = end.bairro;
            if (!dados.enderecocep && end.cep) atualizacao.enderecocep = end.cep;
            if (!dados.email && receita.contato?.email) atualizacao.email = receita.contato.email;
            if (Object.keys(atualizacao).length > 0) {
                setDados({ ...dados, ...atualizacao });
            }

            // Sincroniza CNAEs (merge: adiciona novos, mantém existentes)
            api({
                method: 'post',
                action: '/participante/' + id + '/cnaes/sincronizarReceita',
                token: token,
                data: {
                    cnae_principal: receita.cnae_principal,
                    cnaes_secundarios: receita.cnaes_secundarios,
                    situacao_cadastral: receita.situacao_cadastral,
                    data_inicio_atividade: receita.data_inicio_atividade,
                },
            }).then((respCnaes) => {
                const payload = respCnaes.data?.data ?? {};
                setCnaes(payload.cnaes ?? []);
                const sinc = payload.sincronizacao ?? { adicionados: [], mantidos: [] };
                toast.success(
                    'Dados da Receita: ' + (sinc.adicionados?.length ?? 0) + ' CNAEs novos, '
                    + (sinc.mantidos?.length ?? 0) + ' mantidos.'
                );
            }).catch(() => {
                toast.error('CNAEs recebidos mas falha ao sincronizar no banco.');
            });
        }).catch((err) => {
            const msg = err?.response?.data?.data?.erro
                || err?.response?.data?.message
                || 'Falha ao consultar a Receita Federal.';
            toast.error(msg);
        }).finally(() => {
            setCnpjBuscando(false);
        });
    };

    const salvarCnaeManual = () => {
        const codigo = String(novoCnaeManual.codigo ?? '').trim();
        const descricao = String(novoCnaeManual.descricao ?? '').trim();
        if (!codigo || !descricao) {
            toast.error('Preencha código e descrição do CNAE.');
            return;
        }
        api({
            method: 'post',
            action: '/participante/' + id + '/cnaes',
            token: token,
            data: { codigo, descricao, origem: 'manual', principal: false },
        }).then(() => {
            toast.success('CNAE adicionado.');
            setNovoCnaeManual({ codigo: '', descricao: '' });
            setNovoCnaeManualVisivel(false);
            carregarCnaes();
        }).catch(() => toast.error('Falha ao adicionar CNAE.'));
    };

    const removerCnae = (cnaeId, descricao) => {
        if (!window.confirm('Remover este CNAE e todas as NBSes vinculadas?\n\n' + descricao)) return;
        api({
            method: 'delete',
            action: '/participante/' + id + '/cnaes/' + cnaeId,
            token: token,
        }).then(() => {
            toast.success('CNAE removido.');
            carregarCnaes();
        }).catch(() => toast.error('Falha ao remover CNAE.'));
    };

    const vincularNbsAoCnae = (cnaeId, nbs) => {
        if (!nbs?.codigo) return;
        api({
            method: 'post',
            action: '/fornecedorCnae/' + cnaeId + '/nbs',
            token: token,
            data: {
                codigo: nbs.codigo,
                descricao: nbs.descricao ?? '',
            },
        }).then(() => {
            toast.success('✅ NBS ' + nbs.codigo + ' vinculada.');
            carregarCnaes();
        }).catch((err) => {
            const msg = err?.response?.data?.data?.erro ?? 'Falha ao vincular NBS.';
            toast.error('❌ ' + msg);
        });
    };

    const desvincularNbsDoCnae = (vinculoId) => {
        api({
            method: 'delete',
            action: '/fornecedorCnaeNbs/' + vinculoId,
            token: token,
        }).then(() => {
            toast.success('✅ NBS desvinculada.');
            carregarCnaes();
        }).catch(() => toast.error('❌ Falha ao desvincular NBS.'));
    };

    const carregarSugestoesNbs = (cnaeId) => {
        if (sugestoesPorCnae[cnaeId]) return; // já carregado
        setSugestoesPorCnae({ ...sugestoesPorCnae, [cnaeId]: { carregando: true, lista: [] } });
        api({
            method: 'get',
            action: '/fornecedorCnae/' + cnaeId + '/sugestoesNbs',
            token: token,
        }).then((response) => {
            const payload = response.data?.data ?? {};
            const lista = Array.isArray(payload) ? payload : (payload.sugestoes ?? []);
            setSugestoesPorCnae((prev) => ({
                ...prev,
                [cnaeId]: { carregando: false, lista, tokens: payload.tokens_consultados ?? [] },
            }));
        }).catch(() => {
            setSugestoesPorCnae((prev) => ({ ...prev, [cnaeId]: { carregando: false, lista: [] } }));
        });
    };

    const toggleSelecaoSugestao = (cnaeId, codigo) => {
        const atuais = sugestoesSelecionadasPorCnae[cnaeId] ?? {};
        setSugestoesSelecionadasPorCnae({
            ...sugestoesSelecionadasPorCnae,
            [cnaeId]: { ...atuais, [codigo]: !atuais[codigo] },
        });
    };

    const vincularSugestoesSelecionadas = (cnaeId) => {
        const selecionadas = sugestoesSelecionadasPorCnae[cnaeId] ?? {};
        const sug = sugestoesPorCnae[cnaeId]?.lista ?? [];
        const aVincular = sug.filter((s) => selecionadas[s.codigo]);
        if (aVincular.length === 0) {
            toast.error('Selecione pelo menos uma sugestão.');
            return;
        }
        // Vincula sequencialmente — em paralelo dispararia muitos requests
        const vincularSequencial = (idx) => {
            if (idx >= aVincular.length) {
                toast.success(aVincular.length + ' NBS' + (aVincular.length !== 1 ? 'es' : '') + ' vinculada' + (aVincular.length !== 1 ? 's' : '') + '.');
                setSugestoesSelecionadasPorCnae({ ...sugestoesSelecionadasPorCnae, [cnaeId]: {} });
                // recarrega tudo (sugestões e CNAEs)
                setSugestoesPorCnae((prev) => {
                    const cp = { ...prev };
                    delete cp[cnaeId];
                    return cp;
                });
                carregarCnaes();
                return;
            }
            const nbs = aVincular[idx];
            api({
                method: 'post',
                action: '/fornecedorCnae/' + cnaeId + '/nbs',
                token: token,
                data: { codigo: nbs.codigo, descricao: nbs.descricao },
            }).then(() => vincularSequencial(idx + 1))
              .catch(() => vincularSequencial(idx + 1));
        };
        vincularSequencial(0);
    };

    const adicionarNbsManual = (cnaeId) => {
        const novo = novoNbsManualPorCnae[cnaeId] ?? criarNovoNbsManualVazio();
        const codigo = String(novo.codigo ?? '').trim();
        const descricao = String(novo.descricao ?? '').trim();
        if (!codigo || !descricao) {
            toast.error('Preencha código e descrição da NBS manual.');
            return;
        }

        // 1) Cria/recupera a NBS no catálogo global (com audit do usuário)
        api({
            method: 'post',
            action: '/nbs/manual',
            token: token,
            data: { codigo, descricao },
        }).then(() => {
            // 2) Vincula a NBS ao CNAE
            return api({
                method: 'post',
                action: '/fornecedorCnae/' + cnaeId + '/nbs',
                token: token,
                data: { codigo, descricao },
            });
        }).then(() => {
            toast.success('NBS manual cadastrada e vinculada.');
            setNovoNbsManualPorCnae({ ...novoNbsManualPorCnae, [cnaeId]: criarNovoNbsManualVazio() });
            carregarCnaes();
        }).catch(() => toast.error('Falha ao cadastrar NBS manual.'));
    };

    const carregarBancos = () => {
        if (!id) return;
        api({
            method: 'get',
            action: '/participante/' + id + '/bancos',
            token: token,
        }).then((response) => {
            setBancos(response.data?.data ?? []);
        }).catch(() => {});
    };

    const abrirFormBanco = (banco = null) => {
        if (banco) {
            setBancoEditandoId(banco.id);
            const bd = banco.banco_data;
            setBancoEmEdicao({
                ...criarContaBancariaVazia(),
                ...banco,
                nomeBancoOption: bd ? {
                    value: bd.id,
                    label: (bd.codigo ?? '') + ' - ' + (bd.nome ?? ''),
                } : (banco.nome_banco_snapshot ? {
                    value: banco.banco,
                    label: banco.nome_banco_snapshot,
                } : null),
            });
        } else {
            setBancoEditandoId(null);
            setBancoEmEdicao(criarContaBancariaVazia());
        }
        setNovoBancoVisivel(true);
    };

    const fecharFormBanco = () => {
        setNovoBancoVisivel(false);
        setBancoEditandoId(null);
        setBancoEmEdicao(criarContaBancariaVazia());
    };

    const salvarBanco = () => {
        if (!id) {
            toast.error('Salve o fornecedor antes de cadastrar contas bancárias.');
            return;
        }
        if (!bancoEmEdicao.banco && !bancoEmEdicao.nome_banco_snapshot) {
            toast.error('Selecione o banco.');
            return;
        }

        const payload = {
            banco: bancoEmEdicao.banco || null,
            nome_banco_snapshot: bancoEmEdicao.nomeBancoOption?.label ?? bancoEmEdicao.nome_banco_snapshot ?? '',
            agencia: bancoEmEdicao.agencia,
            conta: bancoEmEdicao.conta,
            tipo_conta: bancoEmEdicao.tipo_conta,
            titular: bancoEmEdicao.titular,
            pix_chave: bancoEmEdicao.pix_chave,
            pix_tipo: bancoEmEdicao.pix_tipo,
            principal: Boolean(bancoEmEdicao.principal),
            observacao: bancoEmEdicao.observacao,
        };

        const requisicao = bancoEditandoId
            ? { method: 'put', action: '/fornecedorBanco/' + bancoEditandoId }
            : { method: 'post', action: '/participante/' + id + '/bancos' };

        api({ ...requisicao, token: token, data: payload })
            .then(() => {
                toast.success(bancoEditandoId ? '✅ Conta bancária atualizada.' : '✅ Conta bancária adicionada.');
                fecharFormBanco();
                carregarBancos();
            })
            .catch(() => toast.error('❌ Falha ao salvar a conta bancária.'));
    };

    const removerBanco = (banco) => {
        if (!window.confirm('Remover a conta bancária ' + (banco.nome_banco_snapshot || 'sem nome') + ' — Ag. ' + (banco.agencia || '?') + ' / CC ' + (banco.conta || '?') + '?')) return;
        api({
            method: 'delete',
            action: '/fornecedorBanco/' + banco.id,
            token: token,
        }).then(() => {
            toast.success('✅ Conta removida.');
            carregarBancos();
        }).catch(() => toast.error('❌ Falha ao remover.'));
    };

    const abrirModalNovoBancoCatalogo = () => {
        setNovoBancoCatalogo({ codigo: '', nome: '' });
        setModalNovoBancoCatalogo(true);
    };

    const fecharModalNovoBancoCatalogo = () => {
        if (salvandoBancoCatalogo) return;
        setModalNovoBancoCatalogo(false);
        setNovoBancoCatalogo({ codigo: '', nome: '' });
    };

    const salvarNovoBancoCatalogo = () => {
        const codigo = String(novoBancoCatalogo.codigo ?? '').trim();
        const nome = String(novoBancoCatalogo.nome ?? '').trim();
        if (!codigo || !nome) {
            toast.error('Preencha código e nome do banco.');
            return;
        }

        setSalvandoBancoCatalogo(true);
        api({
            method: 'post',
            action: '/financeiroBanco',
            token: token,
            data: { codigo, nome },
        }).then((response) => {
            const criado = response.data?.data;
            toast.success('✅ Banco "' + nome + '" cadastrado no catálogo.');
            // já seleciona o banco recém-criado no form da conta
            if (criado?.id) {
                setBancoEmEdicao({
                    ...bancoEmEdicao,
                    banco: criado.id,
                    nomeBancoOption: { value: criado.id, label: criado.codigo + ' - ' + criado.nome },
                    nome_banco_snapshot: criado.codigo + ' - ' + criado.nome,
                });
            }
            // força o AsyncSelect a refazer cache no próximo open
            setBancoSelectKey((k) => k + 1);
            setModalNovoBancoCatalogo(false);
        }).catch((err) => {
            const msg = err?.response?.data?.message ?? 'Falha ao cadastrar banco.';
            toast.error('❌ ' + msg);
        }).finally(() => {
            setSalvandoBancoCatalogo(false);
        });
    };

    const marcarBancoComoPrincipal = (banco) => {
        api({
            method: 'post',
            action: '/fornecedorBanco/' + banco.id + '/marcarPrincipal',
            token: token,
        }).then(() => {
            toast.success('✅ Conta marcada como principal.');
            carregarBancos();
        }).catch(() => toast.error('❌ Falha ao marcar como principal.'));
    };

    // Atalho para os campos das secoes 4/5/6: sao ~20 e o spread inline em cada
    // onChange ja tinha causado engano em tela irma (esquecer o spread zera o resto
    // do objeto).
    const setCF = (campo, valor) => setClassificacaoFiscal({ ...classificacaoFiscal, [campo]: valor });

    // O ReactSelect precisa do objeto {value,label} para pintar o rotulo. Passar so
    // {value} mostra o select vazio mesmo com dado gravado.
    const opcaoAtual = (lista, valor) => opcoesDe(lista).find(o => o.value === valor) || null;

    /**
     * Liga/desliga "nenhuma retenção incide", limpando os tributos ao ligar.
     *
     * Mesma exclusão mútua do formulário público (`marcarSemRetencoes`): declarar
     * ausência e marcar tributo são respostas contraditórias, e quem resolve isso é
     * a tela, não o operador lembrando de desmarcar um por um.
     */
    const marcarSemRetencoes = (marcado) => setClassificacaoFiscal((atual) => {
        if (!marcado) {
            return { ...atual, sem_retencoes: false };
        }

        const limpo = { ...atual, sem_retencoes: true };
        Object.keys(limpo).forEach((campo) => {
            if (campo.indexOf('retem_') === 0) {
                limpo[campo] = false;
            }
        });

        return limpo;
    });

    const semRetencoes = Boolean(classificacaoFiscal.sem_retencoes);

    const renderTributoCard = (config) => {
        const { flag, aliq, esfera, label } = config;
        const ativo = Boolean(classificacaoFiscal[flag]);
        const valor = classificacaoFiscal[aliq];

        return (
            <div className="col-sm-4 mb-3" key={flag}>
                <div style={{
                    background: ativo ? '#e8f5e9' : '#f8f9fa',
                    border: '1px solid ' + (ativo ? '#66bb6a' : '#dee2e6'),
                    borderRadius: 8,
                    padding: '1rem',
                    transition: 'all 0.2s',
                    height: '100%',
                }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: ativo ? '#1b5e20' : '#495057' }}>
                                {label}
                            </div>
                            <small className="text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {classificacaoFiscal[esfera] || '—'}
                            </small>
                        </div>
                        <label className="switch mb-0" style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: semRetencoes ? 'not-allowed' : 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={ativo}
                                disabled={semRetencoes}
                                onChange={(e) => setClassificacaoFiscal({ ...classificacaoFiscal, [flag]: e.target.checked })}
                                style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, background: ativo ? '#4caf50' : '#bdbdbd', borderRadius: 24, transition: '0.2s' }} />
                            <span style={{ position: 'absolute', top: 2, left: ativo ? 22 : 2, width: 20, height: 20, background: '#fff', borderRadius: '50%', transition: '0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                        </label>
                    </div>

                    {ativo ? (
                        <>
                            <label className="small text-muted mb-1" style={{ fontWeight: 600 }}>Alíquota</label>
                            <div className="d-flex align-items-center">
                                <input
                                    type="text"
                                    className="fin-pp__input"
                                    style={{ paddingLeft: 14 }}
                                    value={valor ?? ''}
                                    onChange={(e) => setClassificacaoFiscal({ ...classificacaoFiscal, [aliq]: e.target.value })}
                                    placeholder="0,00"
                                />
                                <span className="ml-2" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1b5e20' }}>%</span>
                            </div>
                            <input
                                type="text"
                                className="fin-pp__input mt-2"
                                style={{ paddingLeft: 14, fontSize: '0.8rem', background: 'rgba(255,255,255,0.7)' }}
                                value={classificacaoFiscal[esfera] ?? ''}
                                onChange={(e) => setClassificacaoFiscal({ ...classificacaoFiscal, [esfera]: e.target.value })}
                                placeholder="Esfera / observação"
                            />
                            {/* O que o FORNECEDOR declarou sobre esta retenção, gravado
                                na aprovação da submissão. Só leitura: o campo acima é
                                do operador, este é declaração dele. Sem isto o texto
                                ficava no banco e ninguém mais o via depois que a
                                submissão saía da fila. */}
                            {classificacaoFiscal[esfera.replace('esfera_', 'observacao_')] && (
                                <div
                                    className="mt-2"
                                    style={{ fontSize: '0.72rem', color: 'var(--fin-mt)', lineHeight: 1.5 }}
                                >
                                    <span style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Declarado:{' '}
                                    </span>
                                    {classificacaoFiscal[esfera.replace('esfera_', 'observacao_')]}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="text-muted small mt-3" style={{ fontStyle: 'italic' }}>Não retido</div>
                            {/* A observação aparece TAMBÉM aqui, e não só no ramo retido.
                                A observação típica é "retido pela tomadora até 31/12/2026" —
                                ou seja, é exatamente sobre um tributo que hoje não incide.
                                Escondê-la no caso "não retido" apagava da tela justamente a
                                explicação de por que ele não incide. */}
                            {classificacaoFiscal[esfera.replace('esfera_', 'observacao_')] && (
                                <div
                                    className="mt-2"
                                    style={{ fontSize: '0.72rem', color: 'var(--fin-mt)', lineHeight: 1.5 }}
                                >
                                    <span style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Declarado:{' '}
                                    </span>
                                    {classificacaoFiscal[esfera.replace('esfera_', 'observacao_')]}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    const atualizarContato = (index, campo, valor) => {
        const contatos = [...(dados.contatosfornecedor ?? [criarContatoVazio()])];
        contatos[index] = {
            ...(contatos[index] ?? criarContatoVazio()),
            [campo]: valor,
        };
        setDados({ ...dados, contatosfornecedor: contatos });
    };

    const adicionarContato = () => {
        setDados({
            ...dados,
            contatosfornecedor: [...(dados.contatosfornecedor ?? []), criarContatoVazio()],
        });
    };

    const removerContato = (index) => {
        const contatos = [...(dados.contatosfornecedor ?? [])];
        contatos.splice(index, 1);

        setDados({
            ...dados,
            contatosfornecedor: contatos.length > 0 ? contatos : [criarContatoVazio()],
        });
    };

    /**
     * Elege o contato principal — exatamente um, sempre.
     *
     * Desenhado como checkbox, comportamento de escolha única: marcar um desmarca
     * os outros, porque a coluna que ele alimenta é uma só.
     *
     * Clicar no que já está marcado NÃO desmarca. Havendo contato, um tem de ser o
     * principal (o `validar()` recusa o save sem isso), então permitir voltar a zero
     * só criaria um estado que a tela deixa montar e o save recusa.
     */
    const marcarContatoPrincipal = (index) => {
        setDados({
            ...dados,
            contatosfornecedor: (dados.contatosfornecedor ?? []).map((contato, i) => ({
                ...contato,
                principal: i === index,
            })),
        });
    };

    // Campos obrigatórios (marcados com * vermelho no formulário).
    // Valida pelo ESTADO, não pelo DOM: as abas renderizam condicionalmente
    // ({tab === 'geral' && ...}), então document.querySelector('#nome') vinha null
    // quando o usuário estava em outra aba e o salvar quebrava sem nenhum aviso.
    const OBRIGATORIOS = [
        { campo: 'nome', id: 'nome', aba: 'geral', abaLabel: 'Geral', label: 'Razão Social' },
    ];

    // Leva o usuário até o campo: troca de aba e foca depois que a aba renderiza.
    const focarCampo = (aba, id) => {
        setTab(aba);
        setTimeout(() => {
            const el = document.querySelector('#' + id);
            if (el) {
                el.focus();
                if (el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
        }, 300);
    };

    const validar = () => {
        const vazio = (v) => String(v ?? '').trim() === '';

        for (let i = 0; i < OBRIGATORIOS.length; i++) {
            const c = OBRIGATORIOS[i];
            if (vazio(dados[c.campo])) {
                toast.error(`Preencha o campo obrigatório: ${c.label} (aba ${c.abaLabel}).`);
                focarCampo(c.aba, c.id);
                return false;
            }
        }

        // CNPJ ou CPF: ao menos um dos dois.
        if (vazio(dados.cnpj) && vazio(dados.cpf)) {
            toast.error('Preencha o campo obrigatório: CNPJ ou CPF (aba Fiscal).');
            focarCampo('fiscal', 'cnpj');
            return false;
        }

        // Não há trava de contato principal aqui, e é deliberado — ver `contatoPrincipalEmAberto`.
        // A escolha em aberto vira aviso na aba Contatos, não recusa de save.

        return true;
    };

    /**
     * Os contatos que o save de fato envia — os que têm algum conteúdo.
     *
     * A tela sempre nasce com um card em branco, e ele não conta para nada.
     */
    const contatosPreenchidos = (dados.contatosfornecedor ?? []).filter((contato) => (
        contato.cargo || contato.nome || contato.funcao
        || contato.email || contato.celular || contato.observacao
    ));

    /**
     * Há contato mas ninguém foi marcado como principal.
     *
     * Isso não impede salvar. Nenhum cadastro anterior a esta entrega tem a marca, e a aprovação de
     * submissão também não a cria — o formulário público não pergunta qual contato é o principal.
     * Recusar o save puniria o operador por uma decisão que ninguém teve como tomar, num campo que
     * ele talvez nem tenha aberto.
     *
     * O que a tela faz é dizer QUEM o Financeiro está usando enquanto ninguém escolhe: o primeiro
     * da lista, que é o fallback do `saving()` no back. Com o nome à vista, corrigir vira decisão
     * de dois cliques em vez de um erro que trava o Salvar.
     */
    const contatoPrincipalEmAberto = contatosPreenchidos.length > 0
        && !contatosPreenchidos.some((contato) => contato.principal);

    const salvar = () => {
        if (!validar()) {
            return false;
        }

        if (salvando) {
            return false;
        }

        const contatos = (dados.contatosfornecedor ?? [])
            .map((contato) => ({
                cargo: contato.cargo ?? '',
                nome: contato.nome ?? '',
                funcao: contato.funcao ?? '',
                email: contato.email ?? '',
                celular: contato.celular ?? '',
                observacao: contato.observacao ?? '',
                principal: Boolean(contato.principal),
            }))
            .filter((contato) => (
                contato.cargo ||
                contato.nome ||
                contato.funcao ||
                contato.email ||
                contato.celular ||
                contato.observacao
            ));

        // As colunas legadas `contato`, `email`, `celular` e `telefone` NÃO são mais
        // derivadas do primeiro contato.
        //
        // Elas não têm campo nesta tela: eram preenchidas por dedução, e o disparo
        // partia da `email` — então quem estivesse em primeiro na lista virava o
        // destinatário do formulário de cadastro, sem ninguém ter escolhido isso. Na
        // prática era o comercial. O destinatário agora sai do PAPEL do contato (ver
        // `FornecedorDisparoController::destinatariosDe()`), e o que havia na coluna
        // virou um contato sem categoria pela migração `2026_09_03_110000`.
        //
        // As quatro saem do payload explicitamente: elas VÊM no GET (são colunas de
        // `participante`), e o spread abaixo as devolveria ao servidor. Chave ausente
        // o `$request->only()` não inclui, então omiti-las é o que faz este save
        // deixar de tocar nelas — nem escreve, nem apaga.
        const { email: _email, contato: _contato, celular: _celular, telefone: _telefone, ...dadosSemContatoFixo } = dados;

        const payload = {
            ...dadosSemContatoFixo,
            tipoCadastro: 'orcamento',
            fornecedorgrupos: dados.fornecedorgrupos ?? [],
            fornecedorgrupo: (dados.fornecedorgrupos ?? [])[0] ?? null,
            tiposervico: dados.tiposervico || null,
            subservicos: (dados.subservicosOptions ?? []).map((opcao) => opcao.value),
            tipoeventofornecedor: dados.tipoeventofornecedor || null,
            prazopagamento: dados.prazopagamento === '' ? null : dados.prazopagamento,
            contatosfornecedor: contatos,
            sede: Boolean(dados.sede),
            // Só os números: a máscara deixa `-` e `_` no texto, e o servidor confere os 8 dígitos.
            enderecocep: dados.enderecocep ? String(dados.enderecocep).replace(/\D/g, '') : null,
        };

        const requisicao = id ? {
            method: 'put',
            action: '/participante/' + id,
        } : {
            method: 'post',
            action: '/participante',
        };

        setSalvando(true);

        api({
            ...requisicao,
            token: token,
            data: payload,
        })
            .then(async function (response) {
                const participanteId = id || response?.data?.data?.id;
                let fiscalOk = true;
                if (participanteId) {
                    const classificacaoPayload = {
                        ...classificacaoFiscal,
                        data_transicao_iva: classificacaoFiscal.data_transicao_iva || null,
                        // `null`, NÃO `''`. A coluna é numérica e o `strict` do
                        // `config/database.php` está desligado — string vazia não dá
                        // erro, o MySQL grava 0 em silêncio, e "não definida" viraria
                        // uma periodicidade de zero mês.
                        periodicidade_meses: classificacaoFiscal.periodicidade_meses || null,
                        // As três datas da seção 5. Mesmo motivo: coluna `date`, e
                        // string vazia com o `strict` desligado não dá erro, grava lixo.
                        vigencia_opcao: classificacaoFiscal.vigencia_opcao || null,
                        data_opcao_simples: classificacaoFiscal.data_opcao_simples || null,
                        opcao_proxima_limite: classificacaoFiscal.opcao_proxima_limite || null,
                        classificacao_credito: classificacaoFiscal.classificacao_credito || null,
                        credito_percentual: classificacaoFiscal.credito_percentual || null,
                    };
                    CLASSIFICACAO_SOMENTE_LEITURA.forEach((campo) => delete classificacaoPayload[campo]);
                    fiscalOk = await api({
                        method: 'put',
                        action: '/participante/' + participanteId + '/classificacaoFiscal',
                        token: token,
                        data: classificacaoPayload,
                    }).then(() => true).catch(() => false);
                }

                if (fiscalOk) {
                    toast.success('✅ Fornecedor salvo com sucesso');
                } else {
                    toast.warning('Fornecedor salvo, mas as retenções fiscais não puderam ser atualizadas. Tente novamente.');
                }

                if (!id && participanteId) {
                    // Fornecedor novo — muda a URL para a tela de edição do recém-criado.
                    // O useEffect com [id] refará o load com o novo id.
                    // Rota correta é /cadastroFornecedor/:id (a antiga '/fornecedor/'+id
                    // não existe em routes.js e caía em "página não encontrada").
                    history.replace('/cadastroFornecedor/' + participanteId, { tipoCadastro: tipoCadastro });
                }
                // Para fornecedor existente: permanece na tela. Estado local já está atualizado.
            })
            .catch(function (error) {
                const mensagemErro = error?.response?.data?.message
                    || error?.response?.data?.error
                    || 'Não foi possível salvar o fornecedor';
                toast.error('❌ ' + mensagemErro);
            })
            .finally(function () {
                setSalvando(false);
            });
    };

    useEffect(() => {
        const nomeEl = document.querySelector('#nome');
        if (nomeEl) nomeEl.focus();
        if (tipoServicoOpcoes.length === 0) {
            carregarTipoServicoOpcoes();
        }
        buscar();
    },
        // eslint-disable-next-line
        [id]);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h2 className="fin-pp__title">Cadastro de Fornecedor</h2>
                        <p className="fin-pp__subtitle">{dados.nome || 'Novo Fornecedor'}</p>
                    </div>
                    <div className="fin-pp__actions">
                        <button
                            type="button"
                            className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill"
                            onClick={salvar}
                            disabled={salvando}
                        >
                            {salvando ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>

                {/* O que o FORNECEDOR marcou como "não se aplica" no formulário público.
                    Fica FORA das abas, e não dentro de uma delas, porque as perguntas
                    marcadas cruzam várias — endereço, banco, fiscal. Antes disso só o
                    modal da fila de revisão mostrava; depois que a submissão saía da
                    fila, a declaração ficava só no banco. */}
                {(classificacaoFiscal.nao_aplica ?? []).length > 0 && (
                    <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16, border: '1px solid var(--fin-acb)' }}>
                        <div style={{ fontSize: '0.78rem', color: 'var(--fin-mt)' }}>
                            <span style={{ fontWeight: 600 }}>Marcado pelo fornecedor como não aplicável: </span>
                            {classificacaoFiscal.nao_aplica.map(caminho => rotuloDoCaminhoNaoAplica(caminho)).join(' · ')}
                        </div>
                    </div>
                )}

                <form className="form" noValidate onSubmit={(e) => { salvar(); e.preventDefault(); }}>

                    {/* ── Tabs ── */}
                    <div className="fin-pp__tabs mb-3">
                        {[
                            { key: 'geral', label: 'Geral' },
                            { key: 'pagamento', label: 'Condição de Pagamento' },
                            { key: 'contatos', label: 'Contatos' },
                            { key: 'endereco', label: 'Endereço' },
                            { key: 'fiscal', label: 'Fiscal' },
                            ...(limparCnpj(dados.cnpj).length === 14 ? [{ key: 'cnaeNbs', label: 'CNAE / NBS' }] : []),
                            { key: 'classificacaoFiscal', label: 'Retenções' },
                            { key: 'banco', label: 'Bancos' },
                            { key: 'documentos', label: 'Documentos' },
                            ...(id && podeVerFormulario ? [{ key: 'formulario', label: 'Formulário' }] : []),
                        ].map(({ key, label }) => (
                            <button
                                key={key}
                                type="button"
                                className={'fin-pp__tab' + (tab === key ? ' is-active' : '')}
                                onClick={() => setTab(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* ══════════════════════════════════════════
                        Tab: Geral
                    ══════════════════════════════════════════ */}
                    {tab === 'geral' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaBuilding size={15} /></span>
                                <span className="fin-pp__cphead-title">Dados Gerais</span>
                            </div>
                            {/* Uma linha fixa por .fin-pp__filters: num bloco só, a quebra seguia a
                                largura da tela e o Tipo de Subserviço caía ao lado da Razão Social.
                                flex-start alinha os rótulos quando um multi-select cresce. */}
                            <div className="fin-pp__filters" style={{ alignItems: 'flex-start' }}>
                                <FinField label="Grupo de Fornecedor">
                                    <ReactSelect
                                        isMulti={true}
                                        value={dados.fornecedorgruposOptions ?? []}
                                        onChange={(selecionados) => setDados({
                                            ...dados,
                                            fornecedorgrupos: (selecionados ?? []).map((registro) => registro.value),
                                            fornecedorgruposOptions: selecionados ?? [],
                                            fornecedorgrupo: (selecionados ?? [])[0]?.value ?? null,
                                            nomeFornecedorgrupo: (selecionados ?? [])[0]?.label ?? null,
                                        })}
                                        loadOptions={(input) => {
                                            return api({
                                                method: 'get',
                                                action: '/fornecedorGrupo',
                                                token: token,
                                                params: { nome: input }
                                            })
                                                .then(response => response.data.data.map(registro => ({ value: registro.id, label: registro.nome })))
                                                .catch(function (error) {
                                                    //console.log(error);
                                                });
                                        }}
                                    />
                                </FinField>
                                <FinField label="Situação">
                                    <ReactSelect
                                        value={dados.situacaoRegistro ? { value: dados.situacaoRegistro } : null}
                                        onChange={e => setDados({ ...dados, situacaoRegistro: e.value })}
                                        options={OPCOES_SITUACAO}
                                    />
                                </FinField>
                                <FinField label="Código">
                                    <FinInput
                                        id="codigo"
                                        value={dados.codigo ?? ''}
                                        onChange={e => setDados({ ...dados, codigo: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Categoria">
                                    <ReactSelect
                                        value={dados.categoria ? { value: dados.categoria, label: dados.nomeCategoria } : null}
                                        onChange={e => setDados({ ...dados, categoria: e.value, nomeCategoria: e.label })}
                                        loadOptions={(input) => {
                                            return api({
                                                method: 'get',
                                                action: '/participanteCategoria',
                                                token: token,
                                                params: { nome: input }
                                            })
                                                .then(response => response.data.data.map(registro => ({ value: registro.id, label: registro.nome })))
                                                .catch(function (error) {
                                                    //console.log(error);
                                                });
                                        }}
                                    />
                                </FinField>
                            </div>
                            <div className="fin-pp__filters" style={{ alignItems: 'flex-start' }}>
                                <FinField label="Preferencial">
                                    <ReactSelect
                                        id="preferencial"
                                        value={dados.preferencial ? { value: dados.preferencial } : null}
                                        onChange={e => setDados({ ...dados, preferencial: e.value })}
                                        options={OPCOES_SIM_NAO}
                                    />
                                </FinField>
                                <FinField label="Tipo do Evento">
                                    <ReactSelect
                                        value={dados.tipoeventofornecedor ? { value: dados.tipoeventofornecedor } : null}
                                        onChange={e => setDados({ ...dados, tipoeventofornecedor: e?.value ?? '' })}
                                        options={OPCOES_TIPO_EVENTO}
                                    />
                                </FinField>
                                <FinField label="Tipo de Serviço">
                                    <ReactSelect
                                        options={tipoServicoOpcoes}
                                        value={dados.tiposervicoOption}
                                        onChange={(selecionado) => setDados({
                                            ...dados,
                                            tiposervico: selecionado?.value ?? null,
                                            tiposervicoOption: selecionado ?? null,
                                        })}
                                    />
                                </FinField>
                                <FinField label="Tipo de Subserviço">
                                    <ReactSelect
                                        isMulti
                                        options={tipoServicoOpcoes}
                                        value={dados.subservicosOptions ?? []}
                                        onChange={(selecionados) => setDados({
                                            ...dados,
                                            subservicosOptions: selecionados ?? [],
                                        })}
                                    />
                                </FinField>
                            </div>
                            <div className="fin-pp__filters">
                                {/* `participante.nome`. É a RAZÃO SOCIAL — o formulário
                                    público grava aqui pelo campo `razaosocial`, e o rótulo
                                    "Nome" ao lado de "Nome Fantasia" fazia a coluna errada
                                    parecer a genérica. */}
                                <FinField label="Razão Social" required className="fin-pp__field--half">
                                    <FinInput
                                        id="nome"
                                        value={dados.nome ?? ''}
                                        onChange={e => setDados({ ...dados, nome: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Nome Fantasia" className="fin-pp__field--half">
                                    <FinInput
                                        id="nomefantasia"
                                        value={dados.nomefantasia ?? ''}
                                        onChange={e => setDados({ ...dados, nomefantasia: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="Observações" full>
                                    <FinTextarea
                                        id="observacao"
                                        value={dados.observacao ?? ''}
                                        onChange={e => setDados({ ...dados, observacao: e.target.value })}
                                        style={{ minHeight: '120px' }}
                                    />
                                </FinField>
                                <FinField label="Fornecedor é sede">
                                    <FinChecks>
                                        <FinCheck
                                            id="sede"
                                            label="Sede"
                                            checked={Boolean(dados.sede)}
                                            onChange={e => setDados({ ...dados, sede: e.target.checked })}
                                        />
                                    </FinChecks>
                                </FinField>
                            </div>
                        </div>
                    )}

                    {/* ══════════════════════════════════════════
                        Tab: Condição de Pagamento
                    ══════════════════════════════════════════ */}
                    {tab === 'pagamento' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-title">Condição de Pagamento</span>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="Pagamento Antecipado">
                                    <ReactSelect
                                        value={dados.pagamentoantecipado ? { value: dados.pagamentoantecipado } : null}
                                        onChange={e => setDados({ ...dados, pagamentoantecipado: e.value })}
                                        options={OPCOES_SIM_NAO}
                                    />
                                </FinField>
                                <FinField label="Prazo (em dias)">
                                    <FinInput
                                        id="prazopagamento"
                                        type="number"
                                        value={dados.prazopagamento ?? ''}
                                        onChange={e => setDados({ ...dados, prazopagamento: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Tipo de Recebimento">
                                    <ReactSelect
                                        value={dados.tiporecebimento ? { value: dados.tiporecebimento } : null}
                                        onChange={e => setDados({ ...dados, tiporecebimento: e?.value ?? null })}
                                        options={OPCOES_TIPO_RECEBIMENTO}
                                    />
                                </FinField>
                            </div>
                        </div>
                    )}

                    {/* ══════════════════════════════════════════
                        Tab: Contatos
                    ══════════════════════════════════════════ */}
                    {tab === 'contatos' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ marginBottom: 4 }}>
                                <span className="fin-pp__cphead-title">Contatos</span>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--sm"
                                    style={{ marginLeft: 'auto' }}
                                    onClick={adicionarContato}
                                >
                                    <FaPlus size={12} /> Adicionar
                                </button>
                            </div>
                            <p className="fin-pp__cphead-sub" style={{ marginBottom: 14 }}>
                                O contato marcado como <strong>Principal</strong> é o que o Financeiro usa
                                quando precisa falar com o fornecedor fora do formulário. O disparo do
                                formulário não usa esta marca — ele vai para os contatos de área
                                Fiscal e Financeiro.
                            </p>

                            {/* Escolha em aberto: avisa quem está sendo usado por falta de marca, em vez
                                de recusar o save. Nomear o contato é o que transforma isto em decisão —
                                "marque o principal" não diz ao operador que o comercial está recebendo
                                pedido de nota fiscal hoje. */}
                            {contatoPrincipalEmAberto && (
                                <div className="fin-pp__banner is-warn" style={{ marginBottom: 14 }}>
                                    <span>
                                        Nenhum contato está marcado como <strong>Principal</strong>. Enquanto
                                        ninguém for marcado, o Financeiro usa o primeiro da lista
                                        {(() => {
                                            const primeiro = contatosPreenchidos[0] || {};
                                            const identificacao = String(primeiro.nome ?? '').trim()
                                                || String(primeiro.email ?? '').trim();
                                            return identificacao ? ` — ${identificacao}` : '';
                                        })()}.
                                    </span>
                                </div>
                            )}
                            {(dados.contatosfornecedor ?? []).map((contato, index) => (
                                <div
                                    key={'contato-' + index}
                                    className="fin-pp__card"
                                    style={{
                                        padding: '12px 16px',
                                        marginBottom: 12,
                                        border: '1px solid ' + (contato.principal ? 'var(--fin-ac)' : 'var(--fin-acb)'),
                                    }}
                                >
                                    {/* Cabeçalho do card: quem é o contato, a marca de principal e o
                                        remover. Estavam todos na mesma linha dos campos, junto com a
                                        observação — o que espremia os inputs e deixava a linha
                                        ilegível. */}
                                    <div
                                        className="fin-pp__cphead"
                                        style={{ paddingBottom: 10, marginBottom: 12, borderBottom: '1px solid var(--fin-acb)' }}
                                    >
                                        <span className="fin-pp__cphead-title" style={{ fontSize: 15 }}>
                                            {String(contato.nome ?? '').trim()
                                                || rotuloDe(PAPEIS_CONTATO, contato.cargo)
                                                || 'Contato ' + (index + 1)}
                                        </span>
                                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
                                            <FinCheck
                                                id={'contato-principal-' + index}
                                                label="Principal"
                                                checked={Boolean(contato.principal)}
                                                onChange={() => marcarContatoPrincipal(index)}
                                            />
                                            <button
                                                type="button"
                                                className="fin-pp__btn fin-pp__btn--danger-ghost fin-pp__btn--sm"
                                                title="Remover este contato"
                                                aria-label="Remover este contato"
                                                onClick={() => removerContato(index)}
                                            >
                                                <FaTrash size={12} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="fin-pp__filters">
                                        {/* "Área", não "Cargo": o valor aqui é o papel do
                                            contato (comercial/fiscal/...), o mesmo vocabulário
                                            que o formulário público oferece. Lista em vez de
                                            texto livre porque a seção existe para saber A QUEM
                                            recorrer por assunto, e "financeiro"/"Financeiro"/
                                            "fin." não respondem isso. Cargo antigo digitado à
                                            mão entra como opção própria — ver
                                            `opcoesAreaContato`. */}
                                        <FinField label="Área">
                                            <ReactSelect
                                                options={opcoesAreaContato(contato.cargo)}
                                                value={contato.cargo
                                                    ? { value: contato.cargo, label: rotuloDe(PAPEIS_CONTATO, contato.cargo) }
                                                    : null}
                                                onChange={e => atualizarContato(index, 'cargo', e?.value ?? '')}
                                                isClearable
                                            />
                                        </FinField>
                                        <FinField label="Nome">
                                            <FinInput
                                                value={contato.nome ?? ''}
                                                onChange={e => atualizarContato(index, 'nome', e.target.value)}
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                        {/* O cargo real da pessoa, só para o responsável por
                                            CBS/IBS — mesma condição do formulário público. É o
                                            que separa um funcionário do contador terceirizado,
                                            e por isso o anexo pede só nesse papel. */}
                                        {contato.cargo === 'cbs_ibs' && (
                                            <FinField label="Cargo">
                                                <FinInput
                                                    value={contato.funcao ?? ''}
                                                    onChange={e => atualizarContato(index, 'funcao', e.target.value)}
                                                    style={{ paddingLeft: 14 }}
                                                />
                                            </FinField>
                                        )}
                                        <FinField label="E-mail">
                                            <FinInput
                                                value={contato.email ?? ''}
                                                onChange={e => atualizarContato(index, 'email', e.target.value)}
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                        <FinField label="Celular">
                                            <InputMask
                                                mask="(99) 99999-9999"
                                                value={contato.celular ?? ''}
                                                onChange={e => atualizarContato(index, 'celular', e.target.value)}
                                            >
                                                {(inputProps) => <input {...inputProps} className="fin-pp__input" style={{ paddingLeft: 14 }} />}
                                            </InputMask>
                                        </FinField>
                                    </div>
                                    {/* A observação é texto longo e ficava espremida entre inputs de
                                        44px na mesma linha flex. Linha própria, largura inteira. */}
                                    <div className="fin-pp__filters">
                                        <FinField label="Observação" full>
                                            <FinTextarea
                                                value={contato.observacao ?? ''}
                                                onChange={e => atualizarContato(index, 'observacao', e.target.value)}
                                                style={{ height: 'auto', padding: '10px 14px', minHeight: 72 }}
                                            />
                                        </FinField>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ══════════════════════════════════════════
                        Tab: Endereço
                    ══════════════════════════════════════════ */}
                    {tab === 'endereco' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-title">Endereço</span>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="Rua / Logradouro" grow>
                                    <FinInput
                                        id="enderecologradouro"
                                        value={dados.enderecologradouro ?? ''}
                                        onChange={e => setDados({ ...dados, enderecologradouro: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Número">
                                    <FinInput
                                        id="endereconumero"
                                        value={dados.endereconumero ?? ''}
                                        onChange={e => setDados({ ...dados, endereconumero: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Complemento" grow>
                                    <FinInput
                                        id="enderecocomplemento"
                                        value={dados.enderecocomplemento ?? ''}
                                        onChange={e => setDados({ ...dados, enderecocomplemento: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="CEP">
                                    <InputMask
                                        mask="99999-999"
                                        className="fin-pp__input"
                                        id="enderecocep"
                                        value={dados.enderecocep ?? ''}
                                        onChange={(e) => {
                                            const dadosAtualizados = { ...dados, enderecocep: e.target.value };
                                            preencherCep(e.target.value, dadosAtualizados);
                                            setDados(dadosAtualizados);
                                        }}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Bairro">
                                    <FinInput
                                        id="enderecobairro"
                                        value={dados.enderecobairro ?? ''}
                                        onChange={e => setDados({ ...dados, enderecobairro: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Cidade">
                                    <ReactSelect
                                        value={dados.enderecomunicipio ? { value: dados.enderecomunicipio, label: dados.nomeEnderecomunicipio } : null}
                                        onChange={e => setDados({ ...dados, enderecomunicipio: e.value, nomeEnderecomunicipio: e.label })}
                                        loadOptions={(input) => {
                                            return api({
                                                method: 'get',
                                                action: '/municipio',
                                                token: token,
                                                params: { nome: input }
                                            })
                                                .then(response => response.data.data.map(registro => ({ value: registro.id, label: registro.nome + '-' + registro.uf })))
                                                .catch(function (error) {
                                                    //console.log(error);
                                                });
                                        }}
                                    />
                                </FinField>
                                <FinField label="País">
                                    <ReactSelect
                                        value={dados.enderecopais ? { value: dados.enderecopais, label: dados.nomeEnderecopais } : null}
                                        onChange={e => setDados({ ...dados, enderecopais: e.value, nomeEnderecopais: e.label })}
                                        loadOptions={(input) => {
                                            return api({
                                                method: 'get',
                                                action: '/pais',
                                                token: token,
                                                params: { nome: input }
                                            })
                                                .then(response => response.data.data.map(registro => ({ value: registro.id, label: registro.nome })))
                                                .catch(function (error) {
                                                    //console.log(error);
                                                });
                                        }}
                                    />
                                </FinField>
                                {/* `participante.enderecouf`. O formulário público grava esta
                                    coluna e a preenche sozinho ao escolher o município; aqui
                                    é manual — a Cidade acima já traz a UF embutida no rótulo
                                    (`nome-uf`), então esta tela nunca teve um campo próprio
                                    para editar a coluna. */}
                                <FinField label="UF">
                                    <FinInput
                                        id="enderecouf"
                                        maxLength={2}
                                        value={dados.enderecouf ?? ''}
                                        onChange={e => setDados({ ...dados, enderecouf: e.target.value.toUpperCase() })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                            </div>
                        </div>
                    )}

                    {/* ══════════════════════════════════════════
                        Tab: Fiscal
                    ══════════════════════════════════════════ */}
                    {tab === 'fiscal' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-title">Fiscal</span>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="CNPJ" required grow>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <FinInput
                                            id="cnpj"
                                            value={dados.cnpj ?? ''}
                                            onChange={e => setDados({ ...dados, cnpj: e.target.value })}
                                            style={{ paddingLeft: 14, flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            className="fin-pp__btn"
                                            onClick={buscarReceita}
                                            disabled={cnpjBuscando || limparCnpj(dados.cnpj).length !== 14}
                                            title="Consulta Receita Federal via BrasilAPI: preenche dados em branco e sincroniza CNAEs"
                                        >
                                            <FaSearch size={13} /> {cnpjBuscando ? 'Buscando...' : 'Receita'}
                                        </button>
                                    </div>
                                </FinField>
                                <FinField label="CPF" required>
                                    <FinInput
                                        id="cpf"
                                        value={dados.cpf ?? ''}
                                        onChange={e => setDados({ ...dados, cpf: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                    <div style={{ fontSize: 11, color: 'var(--fin-mt)', marginTop: 3 }}>
                                        Informe <strong>CNPJ ou CPF</strong> (ao menos um).
                                    </div>
                                </FinField>
                                <FinField label="Inscrição Estadual">
                                    <FinInput
                                        id="inscricaoestadual"
                                        value={dados.inscricaoestadual ?? ''}
                                        onChange={e => setDados({ ...dados, inscricaoestadual: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Indicador de Inscrição">
                                    <ReactSelect
                                        value={dados.indicadorinscricao ? { value: dados.indicadorinscricao, label: dados.nomeIndicadorinscricao } : null}
                                        onChange={e => setDados({ ...dados, indicadorinscricao: e.value, nomeIndicadorinscricao: e.label })}
                                        options={OPCOES_INDICADOR_INSCRICAO}
                                    />
                                </FinField>
                                <FinField label="Inscrição Municipal">
                                    <FinInput
                                        id="inscricaomunicipal"
                                        value={dados.inscricaomunicipal ?? ''}
                                        onChange={e => setDados({ ...dados, inscricaomunicipal: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Regime Tributário">
                                    <ReactSelect
                                        value={dados.regimetributario ? { value: dados.regimetributario, label: dados.nomeRegimetributario } : null}
                                        onChange={e => setDados({ ...dados, regimetributario: e.value, nomeRegimetributario: e.label })}
                                        options={OPCOES_REGIME_TRIBUTARIO}
                                    />
                                </FinField>
                                <FinField label="RG">
                                    <FinInput
                                        id="rg"
                                        value={dados.rg ?? ''}
                                        onChange={e => setDados({ ...dados, rg: e.target.value })}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                {/* Seção 3 do anexo. Entravam só pelo formulário público — sem
                                    campo aqui, o operador não via para onde o XML vai nem em que
                                    prazo, e não tinha como corrigir.

                                    Gravam em `fornecedor_classificacao_fiscal` (por isso `setCF`
                                    e não `setDados`), embora apareçam nesta aba junto da
                                    identificação: é onde o operador espera achá-los, ao lado da
                                    razão social, e não enterrados na aba fiscal. A tabela é
                                    escolha de modelagem — a satélite 1x1 do fornecedor —, não
                                    argumento sobre onde o campo faz sentido na tela. */}
                                <FinField label="E-mail para envio de XML e PDF" grow>
                                    <FinInput
                                        id="email_xml"
                                        type="email"
                                        value={classificacaoFiscal.email_xml ?? ''}
                                        onChange={e => setCF('email_xml', e.target.value)}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                                <FinField label="Prazo de envio do XML">
                                    <ReactSelect
                                        options={opcoesDe(PRAZOS_XML)}
                                        placeholder={placeholderDe(PRAZOS_XML)}
                                        value={opcaoAtual(PRAZOS_XML, classificacaoFiscal.prazo_envio_xml)}
                                        onChange={e => setCF('prazo_envio_xml', e?.value ?? '')}
                                        isClearable
                                    />
                                </FinField>
                                {/* Seção 1 do anexo. CNAE principal, CNAEs secundários e
                                    data de constituição que o fornecedor declara NÃO têm
                                    campo aqui — vão para `fornecedor_cnae`
                                    (`origem = 'declarado'`) e aparecem na aba "CNAE / NBS",
                                    ao lado do que veio da Receita ou foi digitado à mão. Só
                                    o porte fica nesta aba, porque não é por-CNAE, é por
                                    fornecedor, e não tem outra tabela onde morar. */}
                                <FinField label="Porte da empresa">
                                    <ReactSelect
                                        options={opcoesDe(PORTES_EMPRESA)}
                                        placeholder={placeholderDe(PORTES_EMPRESA)}
                                        value={opcaoAtual(PORTES_EMPRESA, classificacaoFiscal.porte)}
                                        onChange={e => setCF('porte', e?.value ?? '')}
                                        isClearable
                                    />
                                </FinField>
                                {/* NBS principal — seção 4 do anexo, coluna `nbs` desta
                                    mesma tabela. Não é a aba CNAE/NBS: aquela lista o NBS
                                    por CNAE, vindo da Receita; este é o NBS único que o
                                    fornecedor declarou como principal. */}
                                <FinField label="NBS principal (declarado)">
                                    <FinInput
                                        id="nbs"
                                        value={classificacaoFiscal.nbs ?? ''}
                                        onChange={e => setCF('nbs', e.target.value)}
                                        style={{ paddingLeft: 14 }}
                                    />
                                </FinField>
                            </div>
                        </div>
                    )}

                    {/* ══════════════════════════════════════════
                        Tab: CNAE / NBS
                    ══════════════════════════════════════════ */}
                    {tab === 'cnaeNbs' && limparCnpj(dados.cnpj).length === 14 && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            {!id && (
                                <div className="alert alert-warning">
                                    Salve o fornecedor uma vez para habilitar a gestão de CNAEs e NBSes.
                                </div>
                            )}
                            {id && (
                                <div style={{ padding: '0.5rem 0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--fin-acb)' }}>
                                        <div>
                                            <div className="fin-pp__cphead" style={{ marginBottom: 4 }}>
                                                <span className="fin-pp__cphead-ic"><FaBuilding size={15} /></span>
                                                <span className="fin-pp__cphead-title">CNAEs vinculados</span>
                                            </div>
                                            <small style={{ color: 'var(--fin-mt)' }}>
                                                {cnaes.length} CNAE{cnaes.length !== 1 ? 's' : ''} cadastrado{cnaes.length !== 1 ? 's' : ''}.
                                                Clique no card para expandir e gerenciar as NBSes.
                                            </small>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm" onClick={buscarReceita} disabled={cnpjBuscando}>
                                                <FaSearch size={12} /> {cnpjBuscando ? 'Buscando...' : 'Sincronizar com Receita'}
                                            </button>
                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => setNovoCnaeManualVisivel(!novoCnaeManualVisivel)}>
                                                <FaPlus size={12} /> CNAE manual
                                            </button>
                                        </div>
                                    </div>

                                    {novoCnaeManualVisivel && (
                                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16, border: '2px solid var(--fin-ac)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                <span className="fin-pp__cphead-title" style={{ color: 'var(--fin-ac)' }}>
                                                    <FaPlus size={12} /> Adicionar CNAE manualmente
                                                </span>
                                                <button
                                                    type="button"
                                                    className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm"
                                                    onClick={() => { setNovoCnaeManualVisivel(false); setNovoCnaeManual({ codigo: '', descricao: '' }); }}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                            <div className="fin-pp__filters">
                                                <FinField label="Código CNAE">
                                                    <FinInput
                                                        placeholder="ex.: 7020-4/00"
                                                        value={novoCnaeManual.codigo}
                                                        onChange={(e) => setNovoCnaeManual({ ...novoCnaeManual, codigo: e.target.value })}
                                                        style={{ paddingLeft: 14 }}
                                                    />
                                                </FinField>
                                                <FinField label="Descrição" grow>
                                                    <FinInput
                                                        placeholder="ex.: Atividades de consultoria em gestão"
                                                        value={novoCnaeManual.descricao}
                                                        onChange={(e) => setNovoCnaeManual({ ...novoCnaeManual, descricao: e.target.value })}
                                                        style={{ paddingLeft: 14 }}
                                                    />
                                                </FinField>
                                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 4 }}>
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm" onClick={salvarCnaeManual}>Salvar</button>
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => { setNovoCnaeManualVisivel(false); setNovoCnaeManual({ codigo: '', descricao: '' }); }}>Cancelar</button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {cnaes.length === 0 && !novoCnaeManualVisivel && (
                                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--fin-mt)', background: 'var(--fin-acw)', borderRadius: 8 }}>
                                            <FaBuilding size={48} style={{ opacity: 0.3, color: 'var(--fin-ac)', marginBottom: 12 }} />
                                            <h5 style={{ color: 'var(--fin-tx)' }}>Nenhum CNAE vinculado</h5>
                                            <p>
                                                Clique em <strong>"Sincronizar com Receita"</strong> para buscar
                                                automaticamente via BrasilAPI,<br />
                                                ou em <strong>"CNAE manual"</strong> para adicionar um código.
                                            </p>
                                        </div>
                                    )}

                                    {cnaes.map((cnae) => {
                                        const expandido = cnaeExpandidoId === cnae.id;
                                        const novoNbs = novoNbsManualPorCnae[cnae.id] ?? criarNovoNbsManualVazio();
                                        const qtdNbs = (cnae.nbses ?? []).length;
                                        return (
                                            <div
                                                key={cnae.id}
                                                className="fin-pp__card"
                                                style={{ marginBottom: 12, border: expandido ? '2px solid var(--fin-ac)' : '1px solid var(--fin-acb)', overflow: 'hidden' }}
                                            >
                                                <div
                                                    style={{
                                                        cursor: 'pointer',
                                                        padding: '12px 16px',
                                                        background: expandido ? 'var(--fin-acw)' : '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                    onClick={() => {
                                                        const novoExpandido = expandido ? null : cnae.id;
                                                        setCnaeExpandidoId(novoExpandido);
                                                        if (novoExpandido) {
                                                            carregarSugestoesNbs(cnae.id);
                                                        }
                                                    }}
                                                >
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--fin-tx)' }}>
                                                            <span style={{ color: 'var(--fin-ac)' }}>{cnae.codigo}</span>
                                                            <span style={{ margin: '0 8px', color: 'var(--fin-mt)' }}>—</span>
                                                            <span style={{ fontWeight: 400, color: 'var(--fin-mt)' }}>{cnae.descricao}</span>
                                                        </div>
                                                        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                            {cnae.principal && (
                                                                <span className="fin-pp__pill is-info">Principal</span>
                                                            )}
                                                            {/* Três origens, não duas: 'receita' (sync BrasilAPI), 'manual'
                                                                (operador, botão "CNAE manual") e 'declarado' (o fornecedor,
                                                                pela seção 1 do formulário público — ver aprovar()). */}
                                                            <span className={'fin-pp__pill ' + (cnae.origem === 'receita' ? 'is-info' : (cnae.origem === 'declarado' ? 'is-warn' : 'is-muted'))}>
                                                                {cnae.origem === 'receita' ? 'Receita Federal' : (cnae.origem === 'declarado' ? 'Declarado pelo fornecedor' : 'Manual')}
                                                            </span>
                                                            {cnae.situacao_cadastral && (
                                                                <span className="fin-pp__pill is-muted">{cnae.situacao_cadastral}</span>
                                                            )}
                                                            {/* Mesmo fato da "data de constituição" do anexo, só que por
                                                                linha de CNAE — é onde ela mora desde que a declaração do
                                                                fornecedor passou a gravar aqui em vez de coluna própria. */}
                                                            {cnae.data_inicio_atividade && (
                                                                <span className="fin-pp__pill is-muted">
                                                                    Início: {String(cnae.data_inicio_atividade).substring(0, 10).split('-').reverse().join('/')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 16, gap: 12 }}>
                                                        <span className={'fin-pp__pill ' + (qtdNbs > 0 ? 'is-ok' : 'is-warn')}>
                                                            {qtdNbs} NBS{qtdNbs !== 1 ? 'es' : ''}
                                                        </span>
                                                        <span style={{ color: 'var(--fin-mt)' }}>
                                                            {expandido ? <FaChevronUp /> : <FaChevronDown />}
                                                        </span>
                                                    </div>
                                                </div>

                                                {expandido && (
                                                    <div style={{ padding: '16px', borderTop: '1px solid var(--fin-acb)' }}>
                                                        {/* NBSes vinculadas */}
                                                        <div style={{ marginBottom: 16 }}>
                                                            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--fin-mt)', fontWeight: 600, marginBottom: 8 }}>
                                                                <FaLink style={{ marginRight: 6 }} />
                                                                NBSes vinculadas a este CNAE
                                                            </div>
                                                            {qtdNbs === 0 && (
                                                                <div style={{ color: 'var(--fin-mt)', fontSize: 13, padding: '8px 12px', background: 'var(--fin-sf2)', borderRadius: 6 }}>
                                                                    Nenhuma NBS vinculada ainda. Use os campos abaixo para adicionar.
                                                                </div>
                                                            )}
                                                            {qtdNbs > 0 && (cnae.nbses ?? []).map((nbs) => (
                                                                <div
                                                                    key={nbs.id}
                                                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', marginBottom: 6, background: 'var(--fin-acw)', borderRadius: 6, borderLeft: '3px solid var(--fin-ac)' }}
                                                                >
                                                                    <div style={{ flex: 1 }}>
                                                                        <strong style={{ color: 'var(--fin-ac)', marginRight: 10 }}>{nbs.codigo}</strong>
                                                                        <span style={{ color: 'var(--fin-mt)', marginRight: 8 }}>—</span>
                                                                        <span style={{ color: 'var(--fin-tx)' }}>{nbs.descricao}</span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm"
                                                                        onClick={() => desvincularNbsDoCnae(nbs.id)}
                                                                        title="Desvincular NBS"
                                                                    >
                                                                        <FaTimes />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* Sugestões automáticas */}
                                                        {(() => {
                                                            const sug = sugestoesPorCnae[cnae.id];
                                                            const selecionadas = sugestoesSelecionadasPorCnae[cnae.id] ?? {};
                                                            const qtdSelecionadas = Object.values(selecionadas).filter(Boolean).length;
                                                            if (!sug) return null;
                                                            if (sug.carregando) {
                                                                return (
                                                                    <div style={{ marginBottom: 16, padding: 12, textAlign: 'center', color: 'var(--fin-mt)', background: '#f3e8ff', borderRadius: 8 }}>
                                                                        Buscando sugestões automáticas baseadas na descrição do CNAE...
                                                                    </div>
                                                                );
                                                            }
                                                            if (sug.lista.length === 0) return null;
                                                            return (
                                                                <div style={{ marginBottom: 16, padding: 12, background: '#f3e8ff', borderRadius: 8, border: '1px solid #c084fc' }}>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                                                        <div>
                                                                            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b21a8', fontWeight: 600, marginBottom: 4 }}>
                                                                                ✨ Sugestões automáticas ({sug.lista.length})
                                                                            </div>
                                                                            <small style={{ color: 'var(--fin-mt)' }}>
                                                                                NBSes do catálogo com termos em comum. Confirme manualmente — é assistente, não validador fiscal.
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ maxHeight: 280, overflowY: 'auto', marginTop: 8 }}>
                                                                        {sug.lista.map((s) => (
                                                                            <label
                                                                                key={s.codigo}
                                                                                style={{
                                                                                    display: 'flex', alignItems: 'flex-start', padding: '8px', marginBottom: 4,
                                                                                    cursor: 'pointer',
                                                                                    background: selecionadas[s.codigo] ? '#e9d5ff' : '#fff',
                                                                                    borderRadius: 4,
                                                                                    border: '1px solid ' + (selecionadas[s.codigo] ? '#a855f7' : '#e5e7eb'),
                                                                                }}
                                                                            >
                                                                                <input
                                                                                    type="checkbox"
                                                                                    style={{ marginTop: 2, marginRight: 8 }}
                                                                                    checked={Boolean(selecionadas[s.codigo])}
                                                                                    onChange={() => toggleSelecaoSugestao(cnae.id, s.codigo)}
                                                                                />
                                                                                <div style={{ flex: 1 }}>
                                                                                    <div>
                                                                                        <strong style={{ color: '#6b21a8' }}>{s.codigo}</strong>
                                                                                        {' — '}{s.descricao}
                                                                                    </div>
                                                                                    <small style={{ color: 'var(--fin-mt)' }}>
                                                                                        {s.categoria && <span>{s.categoria} · </span>}
                                                                                        {s.score} termo{s.score !== 1 ? 's' : ''} em comum
                                                                                        {s.tokens_match && s.tokens_match.length > 0 && (
                                                                                            <span> ({s.tokens_match.join(', ')})</span>
                                                                                        )}
                                                                                    </small>
                                                                                </div>
                                                                            </label>
                                                                        ))}
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                                                        <button
                                                                            type="button"
                                                                            className="fin-pp__btn fin-pp__btn--sm"
                                                                            style={{ background: '#9333ea', color: '#fff', borderColor: '#9333ea' }}
                                                                            onClick={() => vincularSugestoesSelecionadas(cnae.id)}
                                                                            disabled={qtdSelecionadas === 0}
                                                                        >
                                                                            <FaLink style={{ marginRight: 4 }} /> Vincular {qtdSelecionadas > 0 ? '(' + qtdSelecionadas + ')' : 'selecionadas'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Vincular NBS do catálogo */}
                                                        <div style={{ marginBottom: 16, padding: 12, background: 'var(--fin-acw)', borderRadius: 8 }}>
                                                            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--fin-mt)', fontWeight: 600, marginBottom: 4 }}>
                                                                Vincular NBS do catálogo
                                                            </div>
                                                            <small style={{ color: 'var(--fin-mt)', display: 'block', marginBottom: 8 }}>
                                                                Digite código ou descrição — busca em <strong>1.237 códigos NBS oficiais</strong>.
                                                            </small>
                                                            <ReactSelect
                                                                key={'nbs-select-' + cnae.id + '-' + qtdNbs}
                                                                value={null}
                                                                onChange={(e) => {
                                                                    if (e) {
                                                                        vincularNbsAoCnae(cnae.id, {
                                                                            codigo: e.value,
                                                                            descricao: e.descricao,
                                                                        });
                                                                    }
                                                                }}
                                                                loadOptions={(input) => {
                                                                    return api({
                                                                        method: 'get',
                                                                        action: '/nbs',
                                                                        token: token,
                                                                        params: { q: input, limit: 50 },
                                                                    }).then((response) => (response.data?.data ?? []).map((r) => ({
                                                                        value: r.codigo,
                                                                        descricao: r.descricao,
                                                                        label: r.codigo + ' — ' + r.descricao + (r.categoria ? ' (' + r.categoria + ')' : ''),
                                                                    })));
                                                                }}
                                                                placeholder="Digite código ou descrição (ex.: hospedagem, transporte, 1.1806)..."
                                                                isClearable
                                                            />
                                                        </div>

                                                        {/* NBS manual */}
                                                        <div style={{ marginBottom: 16, padding: 12, background: '#fff8e7', borderRadius: 8, border: '1px dashed #f0ad4e' }}>
                                                            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#8a6d3b', fontWeight: 600, marginBottom: 4 }}>
                                                                <FaPlus style={{ marginRight: 4 }} /> Adicionar NBS manual
                                                            </div>
                                                            <small style={{ color: 'var(--fin-mt)', display: 'block', marginBottom: 8 }}>
                                                                Use quando o código não estiver no catálogo. Será salvo no catálogo global com registro de quem cadastrou.
                                                            </small>
                                                            <div className="fin-pp__filters">
                                                                <FinField label="Código">
                                                                    <FinInput
                                                                        placeholder="ex.: 1.1806.61.00"
                                                                        value={novoNbs.codigo}
                                                                        onChange={(e) => setNovoNbsManualPorCnae({
                                                                            ...novoNbsManualPorCnae,
                                                                            [cnae.id]: { ...novoNbs, codigo: e.target.value },
                                                                        })}
                                                                        style={{ paddingLeft: 14 }}
                                                                    />
                                                                </FinField>
                                                                <FinField label="Descrição" grow>
                                                                    <FinInput
                                                                        placeholder="ex.: Organização de eventos especiais"
                                                                        value={novoNbs.descricao}
                                                                        onChange={(e) => setNovoNbsManualPorCnae({
                                                                            ...novoNbsManualPorCnae,
                                                                            [cnae.id]: { ...novoNbs, descricao: e.target.value },
                                                                        })}
                                                                        style={{ paddingLeft: 14 }}
                                                                    />
                                                                </FinField>
                                                                <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                                                                    <button
                                                                        type="button"
                                                                        className="fin-pp__btn fin-pp__btn--sm"
                                                                        style={{ fontWeight: 600 }}
                                                                        onClick={() => adicionarNbsManual(cnae.id)}
                                                                    >
                                                                        <FaPlus size={12} /> Add
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Remover CNAE */}
                                                        <div style={{ textAlign: 'right', paddingTop: 12, borderTop: '1px solid var(--fin-acb)' }}>
                                                            <button
                                                                type="button"
                                                                className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm"
                                                                onClick={() => removerCnae(cnae.id, cnae.codigo + ' — ' + cnae.descricao)}
                                                            >
                                                                <FaTrash style={{ marginRight: 4 }} /> Remover este CNAE
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ══════════════════════════════════════════
                        Tab: Retenções
                    ══════════════════════════════════════════ */}
                    {tab === 'classificacaoFiscal' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            {(() => {
                                const pccTotal = ['aliquota_pis', 'aliquota_cofins', 'aliquota_csll'].reduce((sum, key) => {
                                    const flagKey = key.replace('aliquota_', 'retem_');
                                    if (!classificacaoFiscal[flagKey]) return sum;
                                    const v = parseFloat(String(classificacaoFiscal[key] ?? '0').replace(',', '.')) || 0;
                                    return sum + v;
                                }, 0).toFixed(2);
                                return (
                                    <div style={{ padding: '0.5rem 0' }}>
                                        {/* Seções 4, 5 e 6 do anexo.
                                            O que o fornecedor declarou. Editável aqui porque
                                            fornecedor que nunca recebe o formulário também
                                            precisa ter perfil tributário, e porque declaração
                                            errada tem que ter onde ser corrigida. */}
                                        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--fin-acb)' }}>
                                            <div className="fin-pp__cphead">
                                                <span className="fin-pp__cphead-title">Objeto do fornecimento</span>
                                            </div>
                                            <div className="fin-pp__filters">
                                                <FinField label="Tipo de fornecedor" grow>
                                                    <ReactSelect
                                                        options={opcoesDe(TIPOS_FORNECEDOR)}
                                                        placeholder={placeholderDe(TIPOS_FORNECEDOR)}
                                                        isClearable
                                                        value={opcaoAtual(TIPOS_FORNECEDOR, classificacaoFiscal.tipo_fornecedor)}
                                                        onChange={(e) => setCF('tipo_fornecedor', e ? e.value : '')}
                                                    />
                                                </FinField>
                                                {/* `opcoesComValoresGravados` e não `opcoesDe`: valor
                                                    gravado que saiu da lista (o `mdfe`, por exemplo)
                                                    não acharia opção, sumiria da tela e o próximo
                                                    Salvar o apagaria sem ninguém decidir isso. */}
                                                <FinField label="Documentos fiscais emitidos" grow>
                                                    <ReactSelect
                                                        isMulti
                                                        options={opcoesComValoresGravados(DOCUMENTOS_FISCAIS, classificacaoFiscal.documentos_fiscais)}
                                                        value={opcoesComValoresGravados(DOCUMENTOS_FISCAIS, classificacaoFiscal.documentos_fiscais)
                                                            .filter(o => (classificacaoFiscal.documentos_fiscais || []).includes(o.value))}
                                                        onChange={(sel) => setCF('documentos_fiscais', (sel || []).map(o => o.value))}
                                                    />
                                                </FinField>
                                                {(classificacaoFiscal.documentos_fiscais || []).includes('outro') && (
                                                    <FinField label="Qual outro documento" grow>
                                                        <FinInput
                                                            id="documentos_fiscais_outro"
                                                            value={classificacaoFiscal.documentos_fiscais_outro ?? ''}
                                                            onChange={e => setCF('documentos_fiscais_outro', e.target.value)}
                                                            style={{ paddingLeft: 14 }}
                                                        />
                                                    </FinField>
                                                )}
                                            </div>
                                            <div className="fin-pp__filters">
                                                <FinField label="Principais itens fornecidos" grow>
                                                    <FinTextarea
                                                        style={{ minHeight: 60 }}
                                                        value={classificacaoFiscal.itens_fornecidos ?? ''}
                                                        onChange={(e) => setCF('itens_fornecidos', e.target.value)}
                                                    />
                                                </FinField>
                                                <FinField label="NCM principais (mercadorias)" grow>
                                                    <FinTextarea
                                                        style={{ minHeight: 60 }}
                                                        placeholder="Um código por linha, ou separados por vírgula"
                                                        value={classificacaoFiscal.ncm ?? ''}
                                                        onChange={(e) => setCF('ncm', e.target.value)}
                                                    />
                                                </FinField>
                                                <FinField label="Municípios / UFs onde executa" grow>
                                                    <FinTextarea
                                                        style={{ minHeight: 60 }}
                                                        placeholder="O IBS é devido no destino — o local de execução altera a apuração"
                                                        value={classificacaoFiscal.locais_execucao ?? ''}
                                                        onChange={(e) => setCF('locais_execucao', e.target.value)}
                                                    />
                                                </FinField>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--fin-acb)' }}>
                                            <div className="fin-pp__cphead">
                                                <span className="fin-pp__cphead-title">Enquadramento tributário</span>
                                            </div>
                                            <div className="fin-pp__filters">
                                                <FinField label="Regime IRPJ / CSLL" grow>
                                                    <ReactSelect
                                                        options={opcoesDe(REGIMES_IRPJ)}
                                                        placeholder={placeholderDe(REGIMES_IRPJ)}
                                                        isClearable
                                                        value={opcaoAtual(REGIMES_IRPJ, classificacaoFiscal.regime_irpj)}
                                                        onChange={(e) => setCF('regime_irpj', e ? e.value : '')}
                                                    />
                                                </FinField>
                                                {classificacaoFiscal.regime_irpj === 'imune_isento' && (
                                                    <FinField label="Fundamento da imunidade ou isenção" grow>
                                                        <FinInput
                                                            value={classificacaoFiscal.regime_irpj_fundamento ?? ''}
                                                            onChange={(e) => setCF('regime_irpj_fundamento', e.target.value)}
                                                        />
                                                    </FinField>
                                                )}
                                            </div>
                                            <div className="fin-pp__filters">
                                                <FinField label="Regime de IBS / CBS" grow>
                                                    <ReactSelect
                                                        options={opcoesDe(REGIMES_IBS_CBS)}
                                                        placeholder={placeholderDe(REGIMES_IBS_CBS)}
                                                        isClearable
                                                        value={opcaoAtual(REGIMES_IBS_CBS, classificacaoFiscal.regime_ibs_cbs)}
                                                        onChange={(e) => setCF('regime_ibs_cbs', e ? e.value : '')}
                                                    />
                                                </FinField>
                                                {/* O mesmo semestre do formulário: o início gravado é o
                                                    primeiro dia, e o fim é o último dia do semestre. */}
                                                <FinField label="Vigência da opção (semestre/ano)">
                                                    <ReactSelect
                                                        options={opcoesSemestre(classificacaoFiscal.vigencia_opcao)}
                                                        placeholder="Não informado"
                                                        isClearable
                                                        value={opcoesSemestre(classificacaoFiscal.vigencia_opcao).find(o => o.value === classificacaoFiscal.vigencia_opcao) || null}
                                                        onChange={(e) => setCF('vigencia_opcao', e ? e.value : '')}
                                                    />
                                                </FinField>
                                                <FinField label="Data da opção no Portal">
                                                    <FinInput
                                                        type="date"
                                                        value={classificacaoFiscal.data_opcao_simples ?? ''}
                                                        onChange={(e) => setCF('data_opcao_simples', e.target.value)}
                                                    />
                                                </FinField>
                                                <FinField label="Limite da próxima opção">
                                                    <FinInput
                                                        type="date"
                                                        value={classificacaoFiscal.opcao_proxima_limite ?? ''}
                                                        onChange={(e) => setCF('opcao_proxima_limite', e.target.value)}
                                                    />
                                                </FinField>
                                            </div>
                                            <div className="fin-pp__filters">
                                                <FinField label="Regime específico ou diferenciado" grow>
                                                    <ReactSelect
                                                        options={opcoesDe(REGIMES_ESPECIFICOS)}
                                                        placeholder={placeholderDe(REGIMES_ESPECIFICOS)}
                                                        isClearable
                                                        value={opcaoAtual(REGIMES_ESPECIFICOS, classificacaoFiscal.regime_especifico)}
                                                        onChange={(e) => setCF('regime_especifico', e ? e.value : '')}
                                                    />
                                                </FinField>
                                                {classificacaoFiscal.regime_especifico === 'outro' && (
                                                    <FinField label="Qual regime" grow>
                                                        <FinInput
                                                            value={classificacaoFiscal.regime_especifico_outro ?? ''}
                                                            onChange={(e) => setCF('regime_especifico_outro', e.target.value)}
                                                        />
                                                    </FinField>
                                                )}
                                                <FinField label="Redução, alíquota zero, isenção ou imunidade (qual e fundamento)" grow>
                                                    <FinInput
                                                        placeholder="Ex.: redução de 30%, com a lei e o artigo"
                                                        maxLength={255}
                                                        value={classificacaoFiscal.reducao_percentual ?? ''}
                                                        onChange={(e) => setCF('reducao_percentual', e.target.value)}
                                                    />
                                                </FinField>
                                                <FinField label="Contribuinte de ISS">
                                                    <ReactSelect
                                                        options={opcoesDe(SIM_NAO)}
                                                        placeholder={placeholderDe(SIM_NAO)}
                                                        isClearable
                                                        value={opcaoAtual(SIM_NAO, classificacaoFiscal.contribuinte_iss)}
                                                        onChange={(e) => setCF('contribuinte_iss', e ? e.value : '')}
                                                    />
                                                </FinField>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--fin-acb)' }}>
                                            <div className="fin-pp__cphead">
                                                <span className="fin-pp__cphead-title">Emissão e crédito CBS / IBS</span>
                                            </div>
                                            <small style={{ color: 'var(--fin-mt)' }}>
                                                Divergência entre o CST/cClassTrib informado e o escriturado tende a gerar glosa.
                                            </small>
                                            <div className="fin-pp__filters" style={{ marginTop: 8 }}>
                                                <FinField label="Destaca CBS e IBS nos documentos" grow>
                                                    <ReactSelect
                                                        options={opcoesDe(DESTAQUE_DOCUMENTOS)}
                                                        placeholder={placeholderDe(DESTAQUE_DOCUMENTOS)}
                                                        isClearable
                                                        value={opcaoAtual(DESTAQUE_DOCUMENTOS, classificacaoFiscal.destaque_documentos)}
                                                        onChange={(e) => setCF('destaque_documentos', e ? e.value : '')}
                                                    />
                                                </FinField>
                                                <FinField label="CST habitual">
                                                    <FinInput
                                                        value={classificacaoFiscal.cst ?? ''}
                                                        onChange={(e) => setCF('cst', e.target.value)}
                                                    />
                                                </FinField>
                                                <FinField label="cClassTrib">
                                                    <FinInput
                                                        value={classificacaoFiscal.cclasstrib ?? ''}
                                                        onChange={(e) => setCF('cclasstrib', e.target.value)}
                                                    />
                                                </FinField>
                                            </div>
                                            <div className="fin-pp__filters">
                                                <FinField label="Aplica crédito presumido">
                                                    <ReactSelect
                                                        options={opcoesDe(SIM_NAO)}
                                                        placeholder={placeholderDe(SIM_NAO)}
                                                        isClearable
                                                        value={opcaoAtual(SIM_NAO, classificacaoFiscal.credito_presumido_aplica)}
                                                        onChange={(e) => setCF('credito_presumido_aplica', e ? e.value : '')}
                                                    />
                                                </FinField>
                                            </div>
                                            <div className="fin-pp__filters">
                                                <FinField label="Opera com diferimento ou suspensão">
                                                    <ReactSelect
                                                        options={opcoesDe(SIM_NAO)}
                                                        placeholder={placeholderDe(SIM_NAO)}
                                                        isClearable
                                                        value={opcaoAtual(SIM_NAO, classificacaoFiscal.diferimento_aplica)}
                                                        onChange={(e) => setCF('diferimento_aplica', e ? e.value : '')}
                                                    />
                                                </FinField>
                                                {classificacaoFiscal.diferimento_aplica === 'sim' && (
                                                    <FinField label="Hipótese do diferimento" grow>
                                                        <FinInput
                                                            value={classificacaoFiscal.diferimento_suspensao ?? ''}
                                                            onChange={(e) => setCF('diferimento_suspensao', e.target.value)}
                                                        />
                                                    </FinField>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--fin-acb)' }}>
                                            <div className="fin-pp__cphead">
                                                <span className="fin-pp__cphead-title">Retenções de impostos</span>
                                            </div>
                                            <small style={{ color: 'var(--fin-mt)' }}>
                                                Configure quais tributos são retidos na emissão de NF deste fornecedor.
                                                A esfera tributária é editável conforme particularidades (município, anexo do Simples, etc.).
                                            </small>
                                        </div>

                                        {/* A declaração de AUSÊNCIA da seção 7, o mesmo checkbox do
                                            formulário público. Aqui ele é sobretudo LEITURA do que o
                                            fornecedor afirmou: sem este controle a coluna vinha do
                                            banco, voltava no PUT e ninguém via — dava para marcar um
                                            tributo e salvar um registro que se contradizia. */}
                                        <div style={{ marginBottom: 16 }}>
                                            <FinCheck
                                                id="sem_retencoes"
                                                label="Nenhuma retenção incide sobre as notas deste fornecedor"
                                                checked={semRetencoes}
                                                onChange={(e) => marcarSemRetencoes(e.target.checked)}
                                            />
                                            {semRetencoes && (
                                                <div style={{ fontSize: '0.78rem', color: 'var(--fin-mt)', marginTop: 6 }}>
                                                    Os tributos abaixo ficam travados enquanto isto estiver marcado.
                                                    Desmarque para registrar uma retenção.
                                                </div>
                                            )}
                                        </div>

                                        {/* Tributos individuais */}
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--fin-mt)', fontWeight: 600, marginBottom: 12 }}>
                                                Tributos individuais — Modelo Legado
                                            </div>
                                            <div className="row">
                                                {renderTributoCard({ flag: 'retem_irrf', aliq: 'aliquota_irrf', esfera: 'esfera_irrf', label: 'IRRF' })}
                                                {renderTributoCard({ flag: 'retem_iss', aliq: 'aliquota_iss', esfera: 'esfera_iss', label: 'ISS' })}
                                                {renderTributoCard({ flag: 'retem_inss', aliq: 'aliquota_inss', esfera: 'esfera_inss', label: 'INSS' })}
                                            </div>
                                            <div className="row">
                                                {renderTributoCard({ flag: 'retem_icms_st', aliq: 'aliquota_icms_st', esfera: 'esfera_icms_st', label: 'ICMS / ST' })}
                                                {renderTributoCard({ flag: 'retem_funrural_senar', aliq: 'aliquota_funrural_senar', esfera: 'esfera_funrural_senar', label: 'Funrural / SENAR' })}
                                            </div>
                                        </div>

                                        {/* PCC */}
                                        <div style={{ marginBottom: 16, padding: 12, background: '#fff8e1', border: '1px solid #ffd54f', borderRadius: 10 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                                                <div>
                                                    <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#795548', fontWeight: 600, marginBottom: 4 }}>
                                                        PCC — PIS + COFINS + CSLL
                                                    </div>
                                                    <small style={{ color: 'var(--fin-mt)' }}>
                                                        Agrupador de relatório contábil — não é tributo separado.
                                                    </small>
                                                </div>
                                                <div style={{ background: '#ff9800', color: '#fff', padding: '0.5rem 1rem', borderRadius: 20, fontWeight: 700, fontSize: '1rem' }}>
                                                    Total retido: {pccTotal}%
                                                </div>
                                            </div>
                                            <div className="row">
                                                {renderTributoCard({ flag: 'retem_pis', aliq: 'aliquota_pis', esfera: 'esfera_pis', label: 'PIS' })}
                                                {renderTributoCard({ flag: 'retem_cofins', aliq: 'aliquota_cofins', esfera: 'esfera_cofins', label: 'COFINS' })}
                                                {renderTributoCard({ flag: 'retem_csll', aliq: 'aliquota_csll', esfera: 'esfera_csll', label: 'CSLL' })}
                                            </div>
                                        </div>

                                        {/* IVA Dual */}
                                        <div style={{ marginBottom: 16, padding: 12, background: '#e3f2fd', border: '1px solid #64b5f6', borderRadius: 10 }}>
                                            <div style={{ marginBottom: 12 }}>
                                                <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0d47a1', fontWeight: 600, marginBottom: 4 }}>
                                                    IVA Dual — Reforma Tributária
                                                </div>
                                                <small style={{ color: 'var(--fin-mt)' }}>
                                                    Em transição 2026–2033. Marque apenas se o fornecedor já estiver operando sob o novo regime.
                                                </small>
                                            </div>
                                            <div className="row">
                                                {renderTributoCard({ flag: 'retem_ibs', aliq: 'aliquota_ibs', esfera: 'esfera_ibs', label: 'IBS' })}
                                                {renderTributoCard({ flag: 'retem_cbs', aliq: 'aliquota_cbs', esfera: 'esfera_cbs', label: 'CBS' })}
                                                <div className="col-sm-4 mb-3">
                                                    <div style={{ background: '#fff', border: '1px solid #90caf9', borderRadius: 8, padding: '1rem', height: '100%' }}>
                                                        <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0d47a1', marginBottom: 4 }}>
                                                            Data de transição
                                                        </div>
                                                        <small style={{ color: 'var(--fin-mt)', display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                                            Quando o fornecedor migrou para o IVA Dual
                                                        </small>
                                                        <input
                                                            type="date"
                                                            className="fin-pp__input"
                                                            style={{ paddingLeft: 14 }}
                                                            value={classificacaoFiscal.data_transicao_iva ?? ''}
                                                            onChange={(e) => setClassificacaoFiscal({ ...classificacaoFiscal, data_transicao_iva: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Observação fiscal */}
                                        <div style={{ padding: 12, background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 10 }}>
                                            <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--fin-mt)', fontWeight: 600, marginBottom: 4 }}>
                                                Observação fiscal
                                            </div>
                                            <small style={{ color: 'var(--fin-mt)', display: 'block', marginBottom: 8 }}>
                                                Anotações livres para o financeiro: regime do Simples, particularidades de retenção, contatos, etc.
                                            </small>
                                            <FinField label="Outras retenções" full>
                                                <FinTextarea
                                                    style={{ minHeight: 60 }}
                                                    placeholder="Tributo, alíquota e esfera, se houver algum fora da grade acima"
                                                    value={classificacaoFiscal.retencoes_outras ?? ''}
                                                    onChange={(e) => setCF('retencoes_outras', e.target.value)}
                                                />
                                            </FinField>
                                            <FinTextarea
                                                value={classificacaoFiscal.observacao_fiscal ?? ''}
                                                onChange={(e) => setClassificacaoFiscal({ ...classificacaoFiscal, observacao_fiscal: e.target.value })}
                                                placeholder="Ex.: Optante Simples Nacional Anexo III — ISS retido em São Paulo (5%)"
                                                style={{ minHeight: '80px' }}
                                            />
                                        </div>

                                        {/* Seção 11 — controle cadastral. Avaliação da Access
                                            sobre o regime declarado, nunca preenchida
                                            automaticamente: derivá-la do que o fornecedor
                                            disse tiraria o juízo de quem deve julgar. */}
                                        <div style={{ marginTop: 12, padding: 12, background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 10 }}>
                                            <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--fin-mt)', fontWeight: 600, marginBottom: 4 }}>
                                                Controle cadastral
                                            </div>
                                            <small style={{ color: 'var(--fin-mt)', display: 'block', marginBottom: 8 }}>
                                                Uso interno — nada disto aparece para o fornecedor.
                                            </small>
                                            <div className="row">
                                                <div className="col-sm-4 mb-2">
                                                    <label className="fin-pp__label">Classificação de crédito IBS/CBS</label>
                                                    <select
                                                        className="fin-pp__input"
                                                        value={classificacaoFiscal.classificacao_credito ?? ''}
                                                        onChange={(e) => setClassificacaoFiscal({ ...classificacaoFiscal, classificacao_credito: e.target.value })}
                                                    >
                                                        <option value="">Não classificado</option>
                                                        <option value="integral">Integral</option>
                                                        <option value="reduzido">Reduzido</option>
                                                        <option value="limitado">Limitado (Simples)</option>
                                                        <option value="sem_credito">Sem crédito</option>
                                                    </select>
                                                </div>
                                                <div className="col-sm-4 mb-2">
                                                    <label className="fin-pp__label">Percentual estimado de crédito</label>
                                                    <input
                                                        type="text"
                                                        className="fin-pp__input"
                                                        placeholder="Ex.: 26,5%"
                                                        value={classificacaoFiscal.credito_percentual ?? ''}
                                                        onChange={(e) => setClassificacaoFiscal({ ...classificacaoFiscal, credito_percentual: e.target.value })}
                                                    />
                                                </div>
                                                <div className="col-sm-4 mb-2">
                                                    <label className="fin-pp__label">Periodicidade da revisão</label>
                                                    <select
                                                        className="fin-pp__input"
                                                        value={classificacaoFiscal.periodicidade_meses ?? ''}
                                                        onChange={(e) => setClassificacaoFiscal({ ...classificacaoFiscal, periodicidade_meses: e.target.value })}
                                                    >
                                                        <option value="">Não definida</option>
                                                        <option value="6">6 meses</option>
                                                        <option value="12">12 meses</option>
                                                        <option value="24">24 meses</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* ══════════════════════════════════════════
                        Tab: Bancos
                    ══════════════════════════════════════════ */}

                    {/* ══════════════════════════════════════════
                        Tab: Formulário
                    ══════════════════════════════════════════ */}
                    {tab === 'formulario' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-title">Formulário de cadastro</span>
                                {formulario && (
                                    <span className={'fin-pp__pill ' + pillFormulario(formulario).classe}>
                                        {pillFormulario(formulario).rotulo}
                                    </span>
                                )}
                                {/* Só com submissão aguardando revisão: abre a dela direto na Revisão de Cadastro. */}
                                {formulario?.submissao_pendente && (
                                    <button
                                        type="button"
                                        className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                                        style={{ marginLeft: 'auto' }}
                                        onClick={() => history.push({
                                            pathname: '/fornecedorSubmissao',
                                            search: '?submissao=' + formulario.submissao_pendente,
                                        })}
                                    >
                                        <FaClipboardCheck size={12} /> Revisar cadastro
                                    </button>
                                )}
                            </div>

                            {!formulario ? (
                                <div className="fin-pp__empty">
                                    {formularioCarregando ? 'Carregando...' : 'Não foi possível carregar o formulário.'}
                                </div>
                            ) : (
                                <>
                                    <small style={{ color: 'var(--fin-mt)', display: 'block', marginBottom: 12 }}>
                                        {formulario.status === 'aprovado' && `Cadastro aplicado em ${dataHoraBr(formulario.aplicada_em)}.`}
                                        {formulario.status === 'em_revisao' && `Devolveu o formulário em ${dataHoraBr(formulario.recebida_em)}; aguarda revisão.`}
                                        {formulario.status === 'enviado' && `Recebeu o link por e-mail em ${dataHoraBr(formulario.ultimo_envio_em)} e ainda não devolveu o formulário.`}
                                        {formulario.status === 'gerado' && `Link gerado em ${dataHoraBr(formulario.ultimo_envio_em)} e entregue por fora; o formulário ainda não voltou.`}
                                        {formulario.status === 'nao_enviado' && 'O fornecedor ainda não recebeu o link do formulário.'}
                                    </small>

                                    <div className="fin-pp__filters">
                                        <FinField label="Link do formulário" full>
                                            <FinInput
                                                value={formulario.link ?? ''}
                                                readOnly
                                                placeholder="Nenhum link válido. Gere um novo."
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                    </div>
                                    <small style={{ color: 'var(--fin-mt)', display: 'block', margin: '6px 0 12px' }}>
                                        {formulario.link
                                            ? (formulario.link_origem === 'gerado' ? 'Gerado nesta tela' : 'Enviado por e-mail')
                                                + ` em ${dataHoraBr(formulario.link_criado_em)}`
                                                + (formulario.link_expira_em ? ` · vale até ${dataHoraBr(formulario.link_expira_em)}` : '')
                                            : 'Os links enviados já expiraram ou nenhum foi enviado.'}
                                    </small>

                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                                        <button
                                            type="button"
                                            className="fin-pp__btn fin-pp__btn--sm"
                                            onClick={gerarLinkFormulario}
                                            disabled={gerandoLink}
                                        >
                                            <FaLink size={12} /> {gerandoLink ? 'Gerando...' : 'Gerar novo link'}
                                        </button>
                                    </div>

                                    <div className="fin-pp__cphead">
                                        <span className="fin-pp__cphead-title" style={{ fontSize: 15 }}>Copiar para enviar por fora</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                                        {FORMATOS_COPIA.map(({ key, label }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                className={'fin-pp__btn fin-pp__btn--sm' + (formatoCopia === key ? ' fin-pp__btn--primary' : '')}
                                                onClick={() => setFormatoCopia(key)}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                    <small style={{ color: 'var(--fin-mt)', display: 'block', marginBottom: 10 }}>
                                        {FORMATOS_COPIA.find(formato => formato.key === formatoCopia).ajuda}
                                        {formatoCopia === 'email' && <> Assunto: <strong>{formulario.assunto}</strong></>}
                                    </small>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <button
                                            type="button"
                                            className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                                            disabled={!formulario.link}
                                            onClick={copiarFormulario}
                                        >
                                            <FaCopy size={12} /> Copiar
                                        </button>
                                        <button
                                            type="button"
                                            className="fin-pp__btn fin-pp__btn--sm"
                                            disabled={!formulario.link}
                                            onClick={() => setPreviaFormulario(true)}
                                        >
                                            <FaEye size={12} /> Pré-visualizar
                                        </button>
                                    </div>
                                </>
                            )}

                            {previaFormulario && formulario?.link && (
                                <div
                                    className="modal fade show d-block fin-pp fin-pp__modalbg"
                                    onClick={(e) => { if (e.target === e.currentTarget) setPreviaFormulario(false); }}
                                >
                                    <div className="modal-dialog modal-dialog-centered modal-lg">
                                        <div className="modal-content fin-pp__modal">
                                            <div className="fin-pp__modal-head">
                                                <span className="fin-pp__modal-head-ic"><FaEye size={17} /></span>
                                                <h3 className="fin-pp__modal-title">Pré-visualizar
                                                    <span className="fin-pp__modal-sub">
                                                        {FORMATOS_COPIA.find(formato => formato.key === formatoCopia).label}
                                                    </span>
                                                </h3>
                                                <button type="button" className="fin-pp__modal-close" onClick={() => setPreviaFormulario(false)}>
                                                    <FaTimes size={16} />
                                                </button>
                                            </div>
                                            <div className="modal-body">
                                                {formatoCopia === 'link' ? (
                                                    <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{formulario.link}</div>
                                                ) : (
                                                    // sandbox vazio: sem script e sem navegar — o botão da prévia não abre o formulário
                                                    <iframe
                                                        title="Pré-visualização"
                                                        sandbox=""
                                                        srcDoc={formatoCopia === 'email'
                                                            ? formulario.email_html
                                                            : '<body style="margin:0;padding:24px;">' + formulario.botao_html + '</body>'}
                                                        style={{ width: '100%', height: formatoCopia === 'email' ? 560 : 110, border: '1px solid var(--fin-bd)', borderRadius: 8 }}
                                                    />
                                                )}
                                            </div>
                                            <div className="fin-pp__modal-foot">
                                                <button type="button" className="fin-pp__btn" onClick={() => setPreviaFormulario(false)}>Fechar</button>
                                                <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={copiarFormulario}>
                                                    <FaCopy size={12} /> Copiar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'documentos' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-title">Documentos entregues</span>
                                <span className="fin-pp__cphead-sub">{documentos.length} arquivo(s)</span>
                            </div>
                            <small style={{ color: 'var(--fin-mt)', display: 'block', marginBottom: 10 }}>
                                Anexos da seção 9 enviados pelo fornecedor no formulário de cadastro.
                                Somente leitura aqui: quem anexa é o próprio fornecedor, pelo link que recebeu.
                            </small>
                            {documentos.length === 0 ? (
                                <div className="fin-pp__empty">Nenhum documento entregue por este fornecedor</div>
                            ) : (
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                        <thead>
                                            <tr>
                                                <th>Tipo</th>
                                                <th>Arquivo</th>
                                                <th>Entregue em</th>
                                                <th style={{ width: 60 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {documentos.map(doc => (
                                                <tr key={doc.id}>
                                                    <td className="fin-pp__cell-muted">{rotuloDe(TIPOS_DOCUMENTO, doc.tipo)}</td>
                                                    <td className="fin-pp__cell-strong">{doc.nome || '-'}</td>
                                                    <td className="fin-pp__cell-muted">
                                                        {doc.created_at ? String(doc.created_at).slice(0, 10).split('-').reverse().join('/') : '-'}
                                                    </td>
                                                    <td>
                                                        {doc.arquivo && (
                                                            <FormButton
                                                                method="get"
                                                                small
                                                                title="Baixar documento"
                                                                action={process.env.REACT_APP_API_URL + '/arquivo/download/' + doc.arquivo}
                                                                icon={<FaDownload size={14} />}
                                                                color="primary"
                                                                data={{ token: localStorage.getItem('token') }}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'banco' && (
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--fin-acb)' }}>
                                <div>
                                    <div className="fin-pp__cphead" style={{ marginBottom: 4 }}>
                                        <span className="fin-pp__cphead-ic"><FaUniversity size={15} /></span>
                                        <span className="fin-pp__cphead-title">Contas bancárias</span>
                                    </div>
                                    <small style={{ color: 'var(--fin-mt)' }}>
                                        {bancos.length} conta{bancos.length !== 1 ? 's' : ''} cadastrada{bancos.length !== 1 ? 's' : ''}.
                                        A conta marcada como <strong>principal</strong> é a padrão para pagamentos.
                                    </small>
                                </div>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                                    onClick={() => abrirFormBanco()}
                                    disabled={!id}
                                    title={!id ? 'Salve o fornecedor primeiro' : ''}
                                >
                                    <FaPlus size={12} /> Adicionar conta
                                </button>
                            </div>

                            {!id && (
                                <div className="alert alert-warning">
                                    Salve o fornecedor uma vez para habilitar o cadastro de contas bancárias.
                                </div>
                            )}

                            {/* Formulário inline de banco */}
                            {id && novoBancoVisivel && (
                                <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16, border: '2px solid var(--fin-ac)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <span className="fin-pp__cphead-title" style={{ color: 'var(--fin-ac)' }}>
                                            {bancoEditandoId ? <><FaEdit style={{ marginRight: 4 }} /> Editar conta bancária</> : <><FaPlus style={{ marginRight: 4 }} /> Nova conta bancária</>}
                                        </span>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm" onClick={fecharFormBanco}>
                                            <FaTimes />
                                        </button>
                                    </div>

                                    <div className="fin-pp__filters">
                                        <FinField label="Banco *" grow>
                                            <ReactSelect
                                                key={'banco-select-' + bancoSelectKey}
                                                value={bancoEmEdicao.nomeBancoOption}
                                                onChange={(e) => setBancoEmEdicao({
                                                    ...bancoEmEdicao,
                                                    banco: e?.value ?? null,
                                                    nomeBancoOption: e,
                                                    nome_banco_snapshot: e?.label ?? '',
                                                })}
                                                loadOptions={(input) => {
                                                    return api({
                                                        method: 'get',
                                                        action: '/financeiroBanco',
                                                        token: token,
                                                        params: { nome: input },
                                                    })
                                                        .then((response) => (response.data?.data ?? []).map((r) => ({ value: r.id, label: r.codigo + ' - ' + r.nome })))
                                                        .catch(() => []);
                                                }}
                                                placeholder="Digite código ou nome do banco"
                                                isClearable
                                            />
                                            <small style={{ display: 'block', marginTop: 4 }}>
                                                <button
                                                    type="button"
                                                    onClick={abrirModalNovoBancoCatalogo}
                                                    style={{ background: 'none', border: 'none', padding: 0, color: 'var(--fin-ac)', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.85rem' }}
                                                >
                                                    <FaPlus style={{ fontSize: '0.7rem' }} /> Não encontrou? Cadastrar novo banco no catálogo
                                                </button>
                                            </small>
                                        </FinField>
                                        <FinField label="Tipo de conta">
                                            <ReactSelect
                                                value={OPCOES_TIPO_CONTA.find((o) => o.value === bancoEmEdicao.tipo_conta) ?? null}
                                                onChange={(e) => setBancoEmEdicao({ ...bancoEmEdicao, tipo_conta: e?.value ?? '' })}
                                                options={OPCOES_TIPO_CONTA}
                                                placeholder="Selecione"
                                            />
                                        </FinField>
                                        <FinField label="Agência">
                                            <FinInput
                                                value={bancoEmEdicao.agencia ?? ''}
                                                onChange={(e) => setBancoEmEdicao({ ...bancoEmEdicao, agencia: e.target.value })}
                                                placeholder="0000"
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                        <FinField label="Conta">
                                            <FinInput
                                                value={bancoEmEdicao.conta ?? ''}
                                                onChange={(e) => setBancoEmEdicao({ ...bancoEmEdicao, conta: e.target.value })}
                                                placeholder="00000-0"
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                        <FinField label="Titular (se diferente)" grow>
                                            <FinInput
                                                value={bancoEmEdicao.titular ?? ''}
                                                onChange={(e) => setBancoEmEdicao({ ...bancoEmEdicao, titular: e.target.value })}
                                                placeholder="Em branco = mesmo CNPJ do fornecedor"
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                        <FinField label="Chave PIX" grow>
                                            <FinInput
                                                value={bancoEmEdicao.pix_chave ?? ''}
                                                onChange={(e) => setBancoEmEdicao({ ...bancoEmEdicao, pix_chave: e.target.value })}
                                                placeholder="Opcional"
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                        <FinField label="Tipo PIX">
                                            <ReactSelect
                                                value={OPCOES_TIPO_PIX.find((o) => o.value === bancoEmEdicao.pix_tipo) ?? null}
                                                onChange={(e) => setBancoEmEdicao({ ...bancoEmEdicao, pix_tipo: e?.value ?? '' })}
                                                options={OPCOES_TIPO_PIX}
                                                placeholder="—"
                                                isClearable
                                            />
                                        </FinField>
                                        <FinField label="Conta principal">
                                            <FinChecks>
                                                <FinCheck
                                                    id="conta_principal"
                                                    label="Conta principal"
                                                    checked={Boolean(bancoEmEdicao.principal)}
                                                    onChange={(e) => setBancoEmEdicao({ ...bancoEmEdicao, principal: e.target.checked })}
                                                />
                                            </FinChecks>
                                        </FinField>
                                        <FinField label="Observação" full>
                                            <FinTextarea
                                                value={bancoEmEdicao.observacao ?? ''}
                                                onChange={(e) => setBancoEmEdicao({ ...bancoEmEdicao, observacao: e.target.value })}
                                                placeholder="Ex.: Conta exclusiva para honorários internacionais"
                                                style={{ minHeight: '70px' }}
                                            />
                                        </FinField>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={fecharFormBanco}>Cancelar</button>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm" onClick={salvarBanco}>
                                            <FaSave style={{ marginRight: 4 }} /> {bancoEditandoId ? 'Atualizar' : 'Salvar conta'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {id && bancos.length === 0 && !novoBancoVisivel && (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--fin-mt)', background: 'var(--fin-acw)', borderRadius: 8 }}>
                                    <FaUniversity size={48} style={{ opacity: 0.3, color: 'var(--fin-ac)', marginBottom: 12 }} />
                                    <h5 style={{ color: 'var(--fin-tx)' }}>Nenhuma conta bancária cadastrada</h5>
                                    <p>Clique em <strong>"Adicionar conta"</strong> para incluir a primeira.</p>
                                </div>
                            )}

                            {id && bancos.map((banco) => {
                                const tipo = OPCOES_TIPO_CONTA.find((o) => o.value === banco.tipo_conta);
                                const tipoPix = OPCOES_TIPO_PIX.find((o) => o.value === banco.pix_tipo);
                                return (
                                    <div
                                        key={banco.id}
                                        className="fin-pp__card"
                                        style={{
                                            marginBottom: 12,
                                            padding: '12px 16px',
                                            border: banco.principal ? '2px solid var(--fin-ok)' : '1px solid var(--fin-acb)',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                                            <div style={{ flex: 1, minWidth: 200 }}>
                                                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--fin-tx)', marginBottom: 4 }}>
                                                    <FaUniversity style={{ marginRight: 8, color: 'var(--fin-ac)' }} />
                                                    {banco.banco_data?.codigo ? banco.banco_data.codigo + ' - ' : ''}{banco.banco_data?.nome ?? banco.nome_banco_snapshot ?? 'Banco não identificado'}
                                                </div>
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    {banco.principal && (
                                                        <span className="fin-pp__pill is-ok">
                                                            <FaStar style={{ fontSize: '0.65rem', marginRight: 3 }} /> Principal
                                                        </span>
                                                    )}
                                                    {tipo && <span className="fin-pp__pill is-muted">{tipo.label}</span>}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                {!banco.principal && (
                                                    <button
                                                        type="button"
                                                        className="fin-pp__btn fin-pp__btn--sm"
                                                        onClick={() => marcarBancoComoPrincipal(banco)}
                                                        title="Marcar como principal"
                                                        style={{ borderColor: 'var(--fin-ok)', color: 'var(--fin-ok)' }}
                                                    >
                                                        <FaRegStar />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="fin-pp__btn fin-pp__btn--sm"
                                                    onClick={() => abrirFormBanco(banco)}
                                                    title="Editar conta"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm"
                                                    onClick={() => removerBanco(banco)}
                                                    title="Remover conta"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8 }}>
                                            <div>
                                                <small style={{ color: 'var(--fin-mt)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}>Agência</small>
                                                <div style={{ fontWeight: 600 }}>{banco.agencia || '—'}</div>
                                            </div>
                                            <div>
                                                <small style={{ color: 'var(--fin-mt)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}>Conta</small>
                                                <div style={{ fontWeight: 600 }}>{banco.conta || '—'}</div>
                                            </div>
                                            <div>
                                                <small style={{ color: 'var(--fin-mt)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}>Titular</small>
                                                <div>{banco.titular || <span style={{ color: 'var(--fin-mt)' }}>Mesmo CNPJ do fornecedor</span>}</div>
                                            </div>
                                        </div>

                                        {banco.pix_chave && (
                                            <div style={{ marginTop: 8, padding: '8px 12px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #86efac' }}>
                                                <small style={{ color: 'var(--fin-mt)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}>
                                                    PIX {tipoPix ? '(' + tipoPix.label + ')' : ''}
                                                </small>
                                                <div style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--fin-ok)' }}>
                                                    {banco.pix_chave}
                                                </div>
                                            </div>
                                        )}

                                        {banco.observacao && (
                                            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--fin-mt)', fontStyle: 'italic' }}>
                                                💬 {banco.observacao}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                </form>

                {/* ══════════════════════════════════════════
                    Modal: Cadastrar novo banco no catálogo
                ══════════════════════════════════════════ */}
                {modalNovoBancoCatalogo && (
                    <div
                        className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) fecharModalNovoBancoCatalogo(); }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaUniversity size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Cadastrar novo banco no catálogo</h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={fecharModalNovoBancoCatalogo} disabled={salvandoBancoCatalogo}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <p style={{ color: 'var(--fin-mt)', fontSize: 13, marginBottom: 12 }}>
                                        Este banco ficará disponível para todos os fornecedores futuros. Informe o código FEBRABAN (3 dígitos) e o nome.
                                    </p>
                                    <div className="fin-pp__filters">
                                        <FinField label="Código *">
                                            <FinInput
                                                value={novoBancoCatalogo.codigo}
                                                onChange={(e) => setNovoBancoCatalogo({ ...novoBancoCatalogo, codigo: e.target.value })}
                                                placeholder="ex.: 077"
                                                maxLength={10}
                                                autoFocus
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                        <FinField label="Nome *" full>
                                            <FinInput
                                                value={novoBancoCatalogo.nome}
                                                onChange={(e) => setNovoBancoCatalogo({ ...novoBancoCatalogo, nome: e.target.value })}
                                                placeholder="ex.: Banco Inter S.A."
                                                onKeyDown={(e) => { if (e.key === 'Enter') salvarNovoBancoCatalogo(); }}
                                                style={{ paddingLeft: 14 }}
                                            />
                                        </FinField>
                                    </div>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn" onClick={fecharModalNovoBancoCatalogo} disabled={salvandoBancoCatalogo}>Cancelar</button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={salvarNovoBancoCatalogo} disabled={salvandoBancoCatalogo}>
                                        <FaSave style={{ marginRight: 4 }} /> {salvandoBancoCatalogo ? 'Salvando...' : 'Salvar banco'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErpTemplate>
    );
}
